import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Layout from "@/components/Layout";
import BottomNav, { STEPS, STEPS_ADMIN } from "@/components/BottomNav";
import PhotoUploader from "@/components/PhotoUploader";
import SignatureCanvas from "@/components/SignatureCanvas";
import VoiceRecorder from "@/components/VoiceRecorder";
import CaseDocumentsList from "@/components/CaseDocumentsList";
import CalculationStep from "@/components/CalculationStep";
import ValuationStep from "@/components/ValuationStep";
import HonorarStep from "@/components/HonorarStep";
import BegutachtungStep from "@/components/BegutachtungStep";
import DocumentationStep from "@/components/case/DocumentationStep";
import GutachtenStep from "@/components/case/GutachtenStep";
import DATPdfScanModal from "@/components/case/DATPdfScanModal";
import DATCalculateExpert from "@/components/DATCalculateExpert";
import DATValuateExpert from "@/components/DATValuateExpert";
import InspectionMapsBlock from "@/components/InspectionMapsBlock";
import OverviewStep from "@/components/OverviewStep";
import InspectionQuickBlock from "@/components/InspectionQuickBlock";
import AdditionalPartiesBlock from "@/components/AdditionalPartiesBlock";
import AuthImg from "@/components/AuthImg";
import VehicleDamageMap from "@/components/VehicleDamageMap";
import PhotoGuide from "@/components/PhotoGuide";
import AbtretungsErklaerung from "@/components/AbtretungsErklaerung";
import AnwaltsVollmacht from "@/components/AnwaltsVollmacht";
import CalculationStreamlined from "@/components/case/CalculationStreamlined";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import InsurerCombobox from "@/components/InsurerCombobox";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { ACCIDENT_TEMPLATES, buildAccidentText } from "@/lib/accidentTemplates";
import { Field, StepHeader, VINLiveInfo, PlateLiveInfo, AutoSaveBadge } from "@/components/case/_shared";
import DocumentsStep from "@/components/case/DocumentsStep";
import { ReleasePanel, FinalReportsList } from "@/components/case/ReleasePanel";
import DATCalculationDetails from "@/components/case/DATCalculationDetails";
import DictationModal from "@/components/voice/DictationModal";
import CalculationPhotoSlider from "@/components/CalculationPhotoSlider";
import {
  ChevronLeft, ChevronDown, ChevronUp, Save, Download, Loader2, Sparkles, Car, Search, FileText, CheckCircle2,
  AlertCircle, Upload, Trash2, User, Shield, Camera, PenLine, Sparkle, History, Eye, MapPin,
  Calculator, Mic, FileDown, Send, ShieldCheck,
} from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

// STATUS list is now built dynamically per-render using i18n keys.
// See `getStatusList(t)` inside the component.
const getStatusList = (t) => [
  { v: "draft", l: t("case_editor.status.draft") },
  { v: "in_progress", l: t("case_editor.status.in_progress") },
  { v: "uploaded", l: t("case_editor.status.uploaded") },
  { v: "in_review", l: t("case_editor.status.in_review") },
  { v: "completed", l: t("case_editor.status.completed") },
  { v: "closed", l: t("case_editor.status.closed") },
];

// Render an ISO date (YYYY-MM-DD) as DD.MM.YYYY for display.
const formatGermanDate = (iso) => {
  if (!iso) return "";
  const [y, m, d] = iso.split("-");
  if (!d) return iso;
  return `${d}.${m}.${y}`;
};

// Build a deterministic JSON-snapshot of the editable case fields. Used by the
// auto-save effect to detect "did anything change since the last save?" without
// false-positives from server-only fields like updated_at, history, …
const computeEditableSnapshot = (caseObj) => {
  if (!caseObj) return "";
  const keys = ["title", "case_reference", "order_type", "order_date",
                "customer", "insurance", "vehicle", "inspection", "begutachtung",
                "calculation", "valuation", "ausfall", "honorar", "pre_damages",
                "pre_damages_arrows",
                "abtretung", "damage_description", "ai_generated_report"];
  const out = {};
  for (const k of keys) out[k] = caseObj[k] ?? null;
  return JSON.stringify(out);
};

export default function CaseEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user, accessToken } = useAuth();
  const [c, setC] = useState(null);
  const [step, setStep] = useState("overview");
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [datLoading, setDatLoading] = useState(false);
  const [regenerating, setRegenerating] = useState(false);

  // Auto-save tracking. We snapshot the JSON of all editable fields right
  // after load and after every successful save, then a debounced effect
  // compares the live `c` to it and saves silently if anything changed.
  const lastSavedSnapshot = useRef("");
  const [autoSaveStatus, setAutoSaveStatus] = useState("idle");  // idle | saving | saved | error
  const [lastSavedAt, setLastSavedAt] = useState(null);
  // Track which fields were just auto-filled by the Fahrzeugschein scanner
  // so we can visually highlight them. Keys like "customer.name", "vehicle.vin".
  const [autoFilled, setAutoFilled] = useState(new Set());
  // Kundenfelder im Kunde-Step sind per Default eingeklappt — beim
  // Fahrzeugschein-Scan automatisch ausgeklappt, sonst manuell per Toggle.
  const [customerFieldsExpanded, setCustomerFieldsExpanded] = useState(false);
  // Fahrzeug-Formular: per Default eingeklappt — die Felder erscheinen erst,
  // wenn entweder eine VIN eingetragen wurde ODER der User manuell aufklappt.
  // Ein Ref verhindert, dass der User nach manuellem Einklappen wieder
  // unerwünscht aufgeklappt wird, sobald die VIN existiert.
  const [vehicleExpanded, setVehicleExpanded] = useState(false);
  const vinAutoExpandedRef = useRef(false);
  // Zweiter Collapse-Block: DAT-€-Code + abgeleitete Felder.
  // Standardmäßig immer eingeklappt — der User entscheidet manuell, wann
  // die DAT-Daten sichtbar sein sollen. Admins sehen den Block ohnehin
  // immer (isBO === true).
  const [datBlockExpanded, setDatBlockExpanded] = useState(false);
  // Counter that gets bumped to imperatively open the camera (mobile bottom-nav
  // photo button + desktop StepHeader icon both increment this).
  const [cameraTrigger, setCameraTrigger] = useState(0);
  const [dictationOpen, setDictationOpen] = useState(false);
  const [showAbtretung, setShowAbtretung] = useState(true);
  const [datScanOpen, setDatScanOpen] = useState(false);
  const [datScanInitialFile, setDatScanInitialFile] = useState(null);
  // Kalkulations-Ansicht: neue (streamlined) vs. klassische DAT-Ansicht (Fallback).
  const [calcClassicView, setCalcClassicView] = useState(() => {
    try { return localStorage.getItem("eg_calc_classic") === "1"; } catch { return false; }
  });
  const setCalcView = (classic) => {
    setCalcClassicView(classic);
    try { localStorage.setItem("eg_calc_classic", classic ? "1" : "0"); } catch { /* ignore */ }
  };

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/cases/${id}`);
      // Auto-fill Besichtigungstag with today if the case is still empty.
      // The user can override the date, but the default gets us a reliable
      // "when was the case first opened" marker in the Besichtigung block.
      const loaded = data.case || {};
      const insp = loaded.inspection || {};
      if (!insp.inspection_date) {
        const d = new Date();
        const pad = (n) => String(n).padStart(2, "0");
        const today = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
        loaded.inspection = { ...insp, inspection_date: today };
      }
      setC(loaded);
      lastSavedSnapshot.current = computeEditableSnapshot(loaded);
    } catch (e) {
      toast.error(apiError(e));
      navigate("/dashboard");
    }
  }, [id, navigate]);

  useEffect(() => { load(); }, [load]);

  // Fotos der Akte direkt beim Öffnen serverseitig vorwärmen (Cache) —
  // die PDF-/Bundle-Erstellung ist dann später sofort schnell.
  useEffect(() => {
    if (!id) return;
    api.post(`/cases/${id}/warm-photos`).catch(() => {});
  }, [id]);


  // Auto-save: 2 s nach jeder Änderung, falls etwas dirty ist und der User
  // editieren darf. Skips beim ersten Render (Snapshot match), bei Read-Only
  // und während ein expliziter Save (saveCase) gerade läuft.
  useEffect(() => {
    if (!c) return;
    const canEditNow = c.owner_id === user?.id || ["admin", "backoffice"].includes(user?.role);
    if (!canEditNow) return;
    if (saving) return;  // explicit save in progress — let it finish first
    const snap = computeEditableSnapshot(c);
    if (snap === lastSavedSnapshot.current) {
      // Ensure idle status when truly clean
      if (autoSaveStatus === "saving") setAutoSaveStatus("saved");
      return;
    }
    const t = setTimeout(() => {
      // Re-check inside the timeout in case the user kept typing
      const snapNow = computeEditableSnapshot(c);
      if (snapNow === lastSavedSnapshot.current) {
        setAutoSaveStatus("saved");
        return;
      }
      // "Wird gespeichert…" erst zeigen, wenn der Request WIRKLICH läuft —
      // nicht schon während der 2s-Tipp-Pause (sonst klebt der Badge dauerhaft).
      setAutoSaveStatus("saving");
      saveCase({}, { silent: true });
    }, 2000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [c]);

  // If the current step is admin-only and the user is a Gutachter, bounce to "customer".
  // Also bounce if the step key was removed (e.g. legacy `ausfall` is now in Bewertung).
  // Also bounce desktopOnly steps (z.B. overview/gutachten) auf Mobile-Viewport
  // für Gutachter, damit sie nicht auf einer Bottom-Nav-unsichtbaren Seite stranden.
  // Must live BEFORE the early-return so React-hooks order stays stable.
  const isBORole = ["admin", "backoffice"].includes(user?.role);
  useEffect(() => {
    const cur = STEPS.find((s) => s.key === step);
    if (!cur) {
      // Legacy 'ausfall' key now consolidated into Bewertung.
      setStep(step === "ausfall" ? "valuation" : "customer");
      return;
    }
    if (cur.adminOnly && !isBORole) { setStep("customer"); return; }
    // Mobile-Viewport (< lg = 1024px): desktopOnly-Steps sind in der Bottom-
    // Nav unsichtbar. Wenn ein Gutachter aus Versehen darauf landet (z.B.
    // Default-Step `overview`), schicken wir ihn auf den ersten erreichbaren
    // Tab `customer`. Admin/BO darf auf Mobile auch desktopOnly-Steps sehen
    // (er hat die Drawer-Navigation).
    if (cur.desktopOnly && !isBORole && typeof window !== "undefined" && window.innerWidth < 1024) {
      setStep("customer");
    }
  }, [isBORole, step]);

  // Wenn Admin / Backoffice eine Akte öffnet, die ein Gutachter angelegt hat,
  // markieren wir alle gefüllten Felder cyan, damit sofort sichtbar ist, was
  // bereits eingetragen wurde. Hooks BEFORE early-return.
  const isAdminViewingOthersCase = isBORole && c?.owner_id && c.owner_id !== user?.id;
  const ownerFilled = React.useMemo(() => {
    if (!isAdminViewingOthersCase || !c) return new Set();
    const out = new Set();
    const isFilled = (v) => {
      if (v === "" || v === null || v === undefined) return false;
      if (Array.isArray(v)) return v.length > 0;
      if (typeof v === "object") return Object.keys(v).length > 0;
      if (typeof v === "boolean") return false; // booleans skipped
      return true;
    };
    ["customer", "insurance", "vehicle", "inspection"].forEach((section) => {
      const obj = c[section] || {};
      Object.entries(obj).forEach(([k, v]) => {
        if (isFilled(v)) out.add(`${section}.${k}`);
      });
    });
    return out;
  }, [c, isAdminViewingOthersCase]);

  if (!c) {
    return (
      <Layout title="Akte wird geladen…">
        <div className="flex items-center justify-center py-20 text-zinc-500">
          <Loader2 className="h-5 w-5 animate-spin mr-2" /> Lade Akte…
        </div>
      </Layout>
    );
  }

  const isBO = isBORole;
  const canEdit = c.owner_id === user?.id || isBO;

  // Step-Reihenfolge je Rolle:
  //   • Admin / Backoffice → STEPS_ADMIN  (12 Schritte in der vom Inhaber
  //     festgelegten Reihenfolge: Kunde → Versicherung → Unterschrift → …)
  //   • Gutachter           → STEPS       (Standard-Workflow inkl. Vorschäden)
  //
  // `requiresFinalReport: true` blendet den „Gutachten"-Step nur ein, sobald
  // der Admin mindestens eine finale PDF in `case.final_reports[]` erzeugt
  // hat. Davor sieht der Gutachter den Step nicht — er soll erst aktiv
  // werden, wenn der Admin den Fall zurückgespielt hat.
  const hasFinalReport = (c.final_reports?.length || 0) > 0;
  const stepBase = isBO ? STEPS_ADMIN : STEPS.filter((s) => !s.adminOnly);
  const visibleSteps = stepBase.filter((s) => !s.requiresFinalReport || hasFinalReport);

  const completeness = {
    customer: !!(c.customer?.name && c.customer?.address),
    insurance: !!(c.insurance?.policy_number || (c.insurance?.insurer_name && c.insurance?.claim_number)),
    vehicle: !!(c.vehicle?.vin || c.vehicle?.license_plate),
    begutachtung: !!(c.begutachtung?.zustandsbeschreibung || c.begutachtung?.detaillierte_schilderung || c.begutachtung?.allgemeinzustand),
    inspection: !!(c.inspection?.inspection_date && (c.inspection?.inspection_location || c.inspection?.inspection_city)),
    photos: (c.photos?.length || 0) > 0,
    signature: !!c.signature,
    predamages: Object.values(c.pre_damages || {}).some(
      (p) => (Array.isArray(p?.statuses) && p.statuses.length > 0) || p?.status
    ),
    // Kalkulations-Step gilt als erledigt, sobald entweder Items eingetragen
    // wurden ODER (häufiger Fall) eine DAT-Kalkulations-PDF in den Dokumenten
    // hängt — egal ob auto-generiert nach DAT-Pull-Back oder manuell vom Admin
    // aus dem DAT-Drucken-Dialog hochgeladen.
    calculation:
      (c.calculation?.items?.length || 0) > 0 ||
      (c.documents || []).some((d) => d.doc_type === "dat_calculation"),
    valuation: !!(c.valuation?.replacement_value || c.ausfall?.daily_rate || c.ausfall?.rental_daily_rate || c.ausfall?.towing_cost),
    honorar: !!(c.honorar?.base_fee),
    preview: (c.final_reports?.length || 0) > 0,
    review: !!(c.damage_description || c.ai_generated_report),
  };

  const setField = (section, key, value) => {
    setC((prev) => ({ ...prev, [section]: { ...(prev[section] || {}), [key]: value } }));
    // Remove highlight when user manually edits a field
    setAutoFilled((prev) => {
      if (!prev.has(`${section}.${key}`)) return prev;
      const next = new Set(prev);
      next.delete(`${section}.${key}`);
      return next;
    });
  };

  // Capitalise first letter of every word (space or hyphen separated) — used for names
  const titleCase = (s) =>
    (s || "").replace(/(^|[\s\-])(\p{L})/gu, (_, sep, ch) => sep + ch.toLocaleUpperCase("de-DE"));

  const setNameField = (section, key, value) => setField(section, key, titleCase(value));

  // Style a form input based on whether it was auto-filled (green) or
  // entered by the case-owning Gutachter (cyan). Auto-Felder werden mit
  // sichtbarem grünem Ring + leichtem Background hervorgehoben.
  const hlClass = (path) => {
    if (autoFilled.has(path)) {
      return "border-[#12A9E8]/40 bg-[#12A9E8]/10 ring-2 ring-[#12A9E8]/30 focus:border-[#12A9E8] focus-visible:ring-[#12A9E8]/40";
    }
    if (ownerFilled.has(path)) {
      return "border-cyan-300 bg-cyan-50/30 focus:border-cyan-400 focus-visible:ring-cyan-400/30";
    }
    return "";
  };
  const isHl = (path) => autoFilled.has(path) || ownerFilled.has(path);
  const isAutoFilled = (path) => autoFilled.has(path);

  const applyScan = (data) => {
    const newFilled = new Set();
    const scannedCustomer = {};
    const scannedVehicle = {};
    Object.entries(data.customer || {}).forEach(([k, v]) => {
      if (v) {
        scannedCustomer[k] = ["firstname", "name", "opponent_name"].includes(k) ? titleCase(v) : v;
        newFilled.add(`customer.${k}`);
      }
    });
    Object.entries(data.vehicle || {}).forEach(([k, v]) => {
      if (v !== null && v !== "") {
        scannedVehicle[k] = v;
        newFilled.add(`vehicle.${k}`);
      }
    });

    setC((prev) => ({
      ...prev,
      customer: { ...(prev.customer || {}), ...scannedCustomer },
      vehicle: { ...(prev.vehicle || {}), ...scannedVehicle },
    }));
    setAutoFilled((prev) => {
      const merged = new Set(prev);
      newFilled.forEach((k) => merged.add(k));
      return merged;
    });

    // Kundenfelder automatisch ausklappen, sobald aus dem Fahrzeugschein
    // Daten erkannt wurden — der User sieht so sofort, was übernommen wurde.
    if (newFilled.size > 0) {
      setCustomerFieldsExpanded(true);
    }

    // 🪄 Auto-Speichern direkt nach dem Scan, damit das Backend automatisch
    // das Aktenzeichen (KH-{Initialen} {DDMMYY}) erzeugt und im Header
    // sichtbar wird. Wichtig: wir übergeben die frisch gescannten Werte als
    // `extra`, weil React-State noch nicht geflusht ist.
    const scannedName = (scannedCustomer.name || "").trim();
    if (scannedName && !c?.case_reference) {
      setTimeout(() => {
        saveCase({
          customer: { ...(c.customer || {}), ...scannedCustomer },
          vehicle: { ...(c.vehicle || {}), ...scannedVehicle },
        }).then((updated) => {
          if (updated?.case_reference) {
            toast.success(`Aktenzeichen automatisch generiert: ${updated.case_reference}`);
          }
        }).catch(() => {});
      }, 100);
    }
  };

  const saveCase = async (extra = {}, opts = {}) => {
    const { silent = false } = opts;
    if (!silent) setSaving(true);
    try {
      const payload = {
        title: c.title,
        case_reference: c.case_reference,
        order_type: c.order_type,
        order_date: c.order_date,
        customer: c.customer,
        insurance: c.insurance,
        vehicle: c.vehicle,
        inspection: c.inspection || {},
        begutachtung: c.begutachtung || {},
        calculation: c.calculation || {},
        valuation: c.valuation || {},
        ausfall: c.ausfall || {},
        honorar: c.honorar || {},
        pre_damages: c.pre_damages || {},
        pre_damages_arrows: c.pre_damages_arrows || [],
        damage_description: c.damage_description || "",
        ai_generated_report: c.ai_generated_report || "",
        ...extra,
      };
      const { data } = await api.patch(`/cases/${id}`, payload);
      const saved = data.case;
      // WICHTIG: Nicht blind setC(saved) — der User kann während des Requests
      // weitergetippt haben (Auto-Save läuft im Hintergrund). Editierbare
      // Felder behalten den lokalen Stand, nur server-generierte Felder
      // (Status, Fotos, Referenz, …) werden übernommen. Ist lokal etwas
      // neuer als der Snapshot, speichert der Auto-Save-Effekt erneut.
      setC((prev) => ({
        ...saved,
        title: prev.title,
        order_type: prev.order_type,
        order_date: prev.order_date,
        customer: prev.customer,
        insurance: prev.insurance,
        vehicle: prev.vehicle,
        inspection: prev.inspection,
        begutachtung: prev.begutachtung,
        calculation: prev.calculation,
        valuation: prev.valuation,
        ausfall: prev.ausfall,
        honorar: prev.honorar,
        pre_damages: prev.pre_damages,
        pre_damages_arrows: prev.pre_damages_arrows,
        damage_description: prev.damage_description,
        ai_generated_report: prev.ai_generated_report,
        documentation: prev.documentation,
        case_reference: saved.case_reference || prev.case_reference,
      }));
      // Snapshot = Server-Stand. Weicht der lokale Stand ab (User hat während
      // des Saves getippt), triggert der Auto-Save-Effekt automatisch erneut.
      lastSavedSnapshot.current = computeEditableSnapshot(saved);
      setLastSavedAt(Date.now());
      setAutoSaveStatus("saved");
      if (!silent) toast.success("Gespeichert");
      return data.case;
    } catch (e) {
      setAutoSaveStatus("error");
      if (!silent) toast.error(apiError(e));
    } finally {
      if (!silent) setSaving(false);
    }
  };

  const setStatus = async (status) => {
    await saveCase({ status });
  };

  // Backoffice/Admin: finale PDF im HINTERGRUND erstellen (Job + Polling).
  // Der frühere synchrone Request wurde bei großen Akten vom Proxy gekappt
  // (Cloudflare-Fehler in Produktion) — deshalb sofortige Antwort + Polling.
  const finalizeCase = async () => {
    setSaving(true);
    try {
      await api.post(`/cases/${id}/finalize`);
      const deadline = Date.now() + 180000;
      while (Date.now() < deadline) {
        await new Promise((r) => setTimeout(r, 2500));
        const { data } = await api.get(`/cases/${id}`);
        const fc = data.case;
        if (fc.finalize_status === "error") {
          throw new Error(fc.finalize_error || "PDF-Erstellung fehlgeschlagen");
        }
        if (fc.finalize_status === "done") {
          setC(fc);
          const v = fc.final_reports?.length || 1;
          toast.success(`Gutachten v${v} finalisiert — bereit zur Freigabe`);
          return;
        }
      }
      throw new Error("Zeitüberschreitung — bitte Seite neu laden und Status prüfen");
    } catch (e) {
      toast.error(e?.response ? apiError(e, "Finalisierung fehlgeschlagen") : (e?.message || "Finalisierung fehlgeschlagen"));
    } finally {
      setSaving(false);
    }
  };

  // Gutachter: release / request revision / send to customer
  const releaseCase = async () => {
    setSaving(true);
    try {
      const { data } = await api.post(`/cases/${id}/release`);
      setC(data.case);
      toast.success("Gutachten freigegeben");
    } catch (e) {
      toast.error(apiError(e));
    } finally { setSaving(false); }
  };

  const requestRevision = async (note) => {
    if (!note?.trim()) { toast.error("Bitte Begründung eingeben"); return; }
    setSaving(true);
    try {
      const { data } = await api.post(`/cases/${id}/request-revision`, { note: note.trim() });
      setC(data.case);
      toast.success("Korrektur angefragt");
    } catch (e) {
      toast.error(apiError(e));
    } finally { setSaving(false); }
  };

  const sendToCustomer = async (email) => {
    setSaving(true);
    try {
      const { data } = await api.post(`/cases/${id}/send-to-customer`, { email });
      setC(data.case);
      toast.success(`An Kunden gesendet${email ? ` (${email})` : ""}`);
      return data;  // { magic_link_token, magic_link_path, magic_link_expires_at, ... }
    } catch (e) {
      toast.error(apiError(e));
      return null;
    } finally { setSaving(false); }
  };

  const runDATLookup = async (mode = "auto") => {
    setDatLoading(true);
    try {
      const params = new URLSearchParams();
      const vin = (c.vehicle?.vin || "").trim();
      const hsn = (c.vehicle?.hsn || "").trim();
      const tsn = (c.vehicle?.tsn || "").trim();
      const europa = (c.vehicle?.dat_eur_code || "").trim();
      const plate = (c.vehicle?.license_plate || "").trim();

      // Decide what to query
      if (mode === "vin" || (mode === "auto" && vin.length === 17)) {
        // Client-side VIN validation: DAT requires exactly 17 chars, A-Z/0-9 only (no I/O/Q).
        const cleanVin = vin.toUpperCase();
        if (cleanVin.length !== 17) {
          toast.error("VIN muss genau 17 Zeichen lang sein.");
          return;
        }
        if (!/^[A-HJ-NPR-Z0-9]{17}$/.test(cleanVin)) {
          toast.error("Ungültige VIN: nur A-Z (ohne I, O, Q) und 0-9 erlaubt.");
          return;
        }
        params.set("vin", cleanVin);
      } else if (mode === "kba" || (mode === "auto" && hsn && tsn)) {
        params.set("hsn", hsn);
        params.set("tsn", tsn);
      } else if (mode === "europa" || (mode === "auto" && europa)) {
        params.set("europa_code", europa);
      } else if (plate) {
        params.set("license_plate", plate);
      } else {
        toast.error("Bitte VIN (17 Zeichen), HSN/TSN oder DAT-€-Code eingeben");
        return;
      }

      const { data } = await api.post(`/cases/dat/lookup?${params.toString()}`);
      const v = data.vehicle || {};
      setC((prev) => ({
        ...prev,
        vehicle: {
          ...prev.vehicle,
          ...v,
          dat_data: data.valuation,
        },
      }));
      if (data.hint) {
        const isMockFallback = (data.source || "").toUpperCase().includes("MOCK");
        const toastFn = isMockFallback ? toast.warning : toast.info;
        toastFn(data.hint, { duration: 12000, description: data.source });
      } else {
        const make = v.make || "";
        const model = v.model || "";
        const year = v.year ? ` (${v.year})` : "";
        const vehicleLabel = `${make} ${model}`.trim();
        const title = vehicleLabel
          ? `Fahrzeugdaten von DAT übernommen: ${vehicleLabel}${year}`
          : `Fahrzeugdaten geladen (${data.source})`;
        toast.success(title, vehicleLabel ? { description: data.source } : undefined);
      }
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setDatLoading(false);
    }
  };

  const runGDVLookup = async () => {
    try {
      // EasyExpert24 GDV needs license plate + date_of_loss (DD.MM.YYYY).
      const plate = c.customer?.opponent_plate || c.vehicle?.license_plate || "";
      if (!plate.trim()) {
        toast.error("Bitte Kennzeichen vom Gegner oder eigenes Kennzeichen eingeben");
        return;
      }
      const isoDate = c.insurance?.claim_date || "";
      // Convert YYYY-MM-DD → DD.MM.YYYY for the API.
      let dateOfLoss = "";
      if (isoDate && /^\d{4}-\d{2}-\d{2}/.test(isoDate)) {
        const [y, m, d] = isoDate.split("-");
        dateOfLoss = `${d}.${m}.${y}`;
      }
      toast.message("Versicherungsabfrage läuft …", { duration: 6000 });
      const { data } = await api.post(`/cases/${id}/gdv-lookup`, {
        license_number: plate,
        date_of_loss: dateOfLoss,
      });
      setC(data.case);
      const ins = data.case.insurance || {};
      const veh = data.case.vehicle || {};
      toast.success(`Versicherung: ${ins.insurer_name || veh.make || "Daten erhalten"}`);
    } catch (e) {
      toast.error(apiError(e, "Versicherungsabfrage fehlgeschlagen"));
    }
  };

  const generateReport = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post("/ai/generate-report", { case_id: id });
      setC((prev) => ({ ...prev, ai_generated_report: data.report }));
      toast.success("Gutachtentext generiert");
    } catch (e) {
      toast.error(apiError(e, "Generierung fehlgeschlagen"));
    } finally {
      setGenerating(false);
    }
  };

  const regenerateReference = async () => {
    if (!c.customer?.name?.trim()) {
      toast.error("Bitte zuerst Kundenname eintragen.");
      return;
    }
    setRegenerating(true);
    try {
      const { data } = await api.post(`/cases/${id}/generate-reference`);
      setC((prev) => ({ ...prev, case_reference: data.case_reference }));
      toast.success(`Aktenzeichen aktualisiert: ${data.case_reference}`);
    } catch (e) {
      toast.error(apiError(e, "Konnte Aktenzeichen nicht erzeugen"));
    } finally {
      setRegenerating(false);
    }
  };

  const downloadPDF = async () => {
    try {
      const resp = await api.get(`/cases/${id}/pdf`, {
        responseType: "blob",
        headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
      });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      const safeName = (c.case_reference || c.case_number || id).replace(/[\s/]/g, "_");
      a.download = `Gutachten-${safeName}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("PDF heruntergeladen");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  // ===== Step renderers =====
  const renderCustomer = () => (
    <div className="w-full space-y-5" data-testid="step-customer">
      <StepHeader icon={User} title={t("case_editor.headers.customer_title")} subtitle={t("case_editor.headers.customer_subtitle")} />
      {(user?.role === "gutachter" || user?.role === "admin") && (
        <CaseDocumentsList
          caseId={id}
          documents={c.documents || []}
          onDocumentsChange={(docs) => setC((prev) => ({ ...prev, documents: docs }))}
          onScanned={applyScan}
        />
      )}
      {/* Kollabierbarer Toggle — nur für End-User. Admins überspringen den
          Toggle, die Felder werden unten direkt angezeigt. */}
      {!isBO && (
        <div className="rounded-2xl border border-zinc-200 bg-white overflow-hidden" data-testid="customer-fields-collapsible">
          <button
            type="button"
            onClick={() => setCustomerFieldsExpanded((v) => !v)}
            className="w-full px-5 py-3.5 flex items-center justify-between gap-3 hover:bg-zinc-50/60 transition group"
            data-testid="customer-fields-toggle"
            aria-expanded={customerFieldsExpanded}
          >
            <div className="flex items-center gap-3 min-w-0">
              <div className="h-8 w-8 rounded-lg bg-zinc-100 group-hover:bg-zinc-200 transition flex items-center justify-center shrink-0">
                <User className="h-4 w-4 text-zinc-700" />
              </div>
              <div className="text-left min-w-0">
                <div className="font-display font-semibold text-zinc-900 text-sm">
                  {t("case_editor.sections.edit_customer_data")}
                </div>
                <div className="text-xs text-zinc-500">
                  {customerFieldsExpanded
                    ? t("case_editor.sections.click_to_collapse")
                    : t("case_editor.sections.edit_customer_hint")}
                </div>
              </div>
            </div>
            {customerFieldsExpanded
              ? <ChevronUp className="h-4 w-4 text-zinc-500 shrink-0" />
              : <ChevronDown className="h-4 w-4 text-zinc-500 shrink-0" />}
          </button>
        </div>
      )}
      {(isBO || customerFieldsExpanded) && (
        <div className="grid md:grid-cols-2 gap-4" data-testid="customer-fields-grid">
        <Field label={t("case_editor.fields.firstname")} highlight={isHl("customer.firstname")} autoFilled={isAutoFilled("customer.firstname")}>
          <Input className={hlClass("customer.firstname")} data-testid="customer-firstname" value={c.customer?.firstname || ""} onChange={(e) => setNameField("customer", "firstname", e.target.value)} />
        </Field>
        <Field label={t("case_editor.fields.lastname")} highlight={isHl("customer.name")} autoFilled={isAutoFilled("customer.name")}>
          <Input className={hlClass("customer.name")} data-testid="customer-name" value={c.customer?.name || ""} onChange={(e) => setNameField("customer", "name", e.target.value)} />
        </Field>
        <Field label={t("case_editor.fields.address")} highlight={isHl("customer.address")} autoFilled={isAutoFilled("customer.address")}>
          <Input className={hlClass("customer.address")} data-testid="customer-address" value={c.customer?.address || ""} onChange={(e) => setField("customer", "address", e.target.value)} />
        </Field>
        <div className="grid grid-cols-[1fr_2fr] gap-3">
          <Field label={t("case_editor.fields.postal_code")} highlight={isHl("customer.postal_code")} autoFilled={isAutoFilled("customer.postal_code")}>
            <Input className={hlClass("customer.postal_code")} data-testid="customer-postal" value={c.customer?.postal_code || ""} onChange={(e) => setField("customer", "postal_code", e.target.value)} />
          </Field>
          <Field label={t("case_editor.fields.city")} highlight={isHl("customer.city")} autoFilled={isAutoFilled("customer.city")}>
            <Input className={hlClass("customer.city")} data-testid="customer-city" value={c.customer?.city || ""} onChange={(e) => setField("customer", "city", e.target.value)} />
          </Field>
        </div>
        <Field label={t("case_editor.fields.phone")}>
          <Input data-testid="customer-phone" value={c.customer?.phone || ""} onChange={(e) => setField("customer", "phone", e.target.value)} />
        </Field>
        <Field label={t("case_editor.fields.email")}>
          <Input data-testid="customer-email" type="email" value={c.customer?.email || ""} onChange={(e) => setField("customer", "email", e.target.value)} />
        </Field>
        {/* NEW: Amtl. Kennzeichen, Fahrzeugtyp, Vorsteuer, Auftragstyp */}
        <Field label={t("case_editor.fields.official_plate")} highlight={isHl("vehicle.license_plate")} autoFilled={isAutoFilled("vehicle.license_plate")}>
          <Input className={hlClass("vehicle.license_plate")} data-testid="customer-plate" value={c.vehicle?.license_plate || ""} onChange={(e) => setField("vehicle", "license_plate", e.target.value)} placeholder="M-AB 1234" />
        </Field>
        <Field label={t("case_editor.fields.vehicle_type")} highlight={isHl("vehicle.vehicle_type")} autoFilled={isAutoFilled("vehicle.vehicle_type")}>
          <Select value={c.vehicle?.vehicle_type || "pkw"} onValueChange={(v) => setField("vehicle", "vehicle_type", v)}>
            <SelectTrigger data-testid="customer-vehicle-type" className={hlClass("vehicle.vehicle_type")}><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="pkw">{t("case_editor.fields.vehicle_pkw")}</SelectItem>
              <SelectItem value="lkw">{t("case_editor.fields.vehicle_lkw")}</SelectItem>
              <SelectItem value="motorrad">{t("case_editor.fields.vehicle_motorcycle")}</SelectItem>
              <SelectItem value="anhaenger">{t("case_editor.fields.vehicle_trailer")}</SelectItem>
              <SelectItem value="sonstige">{t("case_editor.fields.vehicle_other")}</SelectItem>
            </SelectContent>
          </Select>
        </Field>
        <Field label={t("case_editor.fields.input_tax_eligible")} highlight={isHl("customer.is_taxable")} autoFilled={isAutoFilled("customer.is_taxable")}>
          <Select value={c.customer?.is_taxable ? "ja" : "nein"} onValueChange={(v) => setField("customer", "is_taxable", v === "ja")}>
            <SelectTrigger data-testid="customer-taxable" className={hlClass("customer.is_taxable")}><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="ja">{t("case_editor.fields.input_tax_yes")}</SelectItem>
              <SelectItem value="nein">{t("case_editor.fields.input_tax_no")}</SelectItem>
            </SelectContent>
          </Select>
        </Field>
        <Field label={t("case_editor.fields.order_type")}>
          <Select value={c.order_type || "haftpflicht"} onValueChange={(v) => setC((prev) => ({ ...prev, order_type: v }))}>
            <SelectTrigger data-testid="customer-order-type"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="haftpflicht">{t("case_editor.fields.order_type_liability")}</SelectItem>
              <SelectItem value="kostenvoranschlag">{t("case_editor.fields.order_type_estimate")}</SelectItem>
              <SelectItem value="wertgutachten">{t("case_editor.fields.order_type_value")}</SelectItem>
            </SelectContent>
          </Select>
        </Field>
      </div>
      )}

      {/* Besichtigungs-Schnellerfassung direkt im Kunden-Schritt:
          Tag (auto-today) + Ort (GPS / manuelle Eingabe). Schreibt in
          inspection.inspection_date / inspection_location / inspection_city. */}
      <InspectionQuickBlock
        value={c.inspection || {}}
        onChange={(next) => setC((prev) => ({ ...prev, inspection: next }))}
        readOnly={!canEdit}
      />
    </div>
  );

  const renderInsurance = () => (
    <div className="w-full space-y-4" data-testid="step-insurance">
      <StepHeader icon={Shield} title={t("case_editor.headers.insurance_title")} subtitle={t("case_editor.headers.insurance_subtitle")} />

      {/* Kompakte Single-Card mit dichten Sub-Sektionen — Reihenfolge:
            1. Unfallgegner & Schaden (zuerst — wichtigste Info)
            2. Weitere Beteiligte (kollabierbar — öffnet sich on demand)
            3. Versicherungsdaten (unten) */}
      <div className="rounded-2xl border border-cyan-100 bg-gradient-to-br from-cyan-50/40 to-white p-4 sm:p-5 space-y-4" data-testid="insurance-card">

        {/* Unfallgegner + Schaden — JETZT GANZ OBEN */}
        <section>
          <SectionLabel>{t("case_editor.sections.opponent_damage")}</SectionLabel>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mt-2">
            <Field label="Schadenart" dense>
              <select
                data-testid="insurance-damage-type"
                className="flex h-9 w-full rounded-md border border-zinc-200 bg-white px-3 py-1 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-cyan-500"
                value={c.insurance?.damage_type || ""}
                onChange={(e) => setField("insurance", "damage_type", e.target.value)}
              >
                <option value="">— bitte wählen —</option>
                <option value="haftpflicht">Haftpflichtschaden</option>
                <option value="kasko">Kaskoschaden</option>
              </select>
            </Field>
            <Field label={t("case_editor.fields.opponent_name")} dense>
              <Input data-testid="opponent-name" value={c.customer?.opponent_name || ""} onChange={(e) => setNameField("customer", "opponent_name", e.target.value)} />
            </Field>
            <Field label={t("case_editor.fields.opponent_plate")} dense>
              <Input data-testid="opponent-plate" value={c.customer?.opponent_plate || ""} onChange={(e) => setField("customer", "opponent_plate", e.target.value)} placeholder="M-AB 1234" />
            </Field>
            <Field label={t("case_editor.fields.damage_location")} dense>
              <Input
                data-testid="insurance-damage-location"
                value={c.insurance?.damage_location || ""}
                onChange={(e) => setField("insurance", "damage_location", e.target.value)}
                placeholder="BAB A8, AS Esslingen"
              />
            </Field>
            <Field label={t("case_editor.fields.claim_date")} dense>
              <Input
                type="date"
                data-testid="insurance-claim-date"
                value={c.insurance?.claim_date || ""}
                onChange={(e) => setField("insurance", "claim_date", e.target.value)}
              />
            </Field>
          </div>
          <div className="mt-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <p className="text-[11px] text-zinc-500">
              {t("case_editor.actions.gdv_hint")}
            </p>
            <Button
              type="button"
              onClick={runGDVLookup}
              size="sm"
              className="brand-gradient-bg text-white h-8 px-3 text-xs sm:w-auto"
              data-testid="gdv-lookup-btn"
            >
              <Search className="h-3.5 w-3.5 mr-1.5" />
              {t("case_editor.actions.gdv_lookup")}
            </Button>
          </div>
          {c.vu_lock?.plate && (
            <div className="mt-2 flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-[11px] text-amber-800" data-testid="vu-lock-banner">
              <ShieldCheck className="h-3.5 w-3.5 shrink-0 mt-0.5" />
              <span>
                <b>Akte gebunden an {c.vu_lock.plate}</b> — Versicherungsabfrage am{" "}
                {(c.vu_lock.first_queried_at || "").slice(0, 10)}. Kennzeichen/FIN sind gesperrt
                (Doppelnutzungs-Schutz). Für ein anderes Fahrzeug bitte eine neue Akte anlegen.
              </span>
            </div>
          )}
          {c.identity_locked_at && !c.vu_lock?.plate && (
            <div className="mt-2 flex items-start gap-2 rounded-lg bg-rose-50 border border-rose-200 px-3 py-2 text-[11px] text-rose-800" data-testid="identity-lock-banner">
              <ShieldCheck className="h-3.5 w-3.5 shrink-0 mt-0.5" />
              <span className="flex-1">
                <b>Akte gesperrt</b> — diese Akte ist einem Schadenfall zugeordnet. FIN/VIN,
                Kennzeichen, Halter-Name und Schadensdatum können nicht mehr geändert werden.
                Für einen anderen Schadenfall bitte eine <Link to="/dashboard" className="underline font-semibold">neue Akte anlegen</Link>.
              </span>
              {(user?.role === "admin" || user?.role === "backoffice") && (
                <button
                  type="button"
                  onClick={async () => {
                    if (!window.confirm("Identity-Lock dieser Akte aufheben? (wird im Audit-Log dokumentiert)")) return;
                    try {
                      await api.post(`/cases/${c.id}/unlock-identity`);
                      toast.success("Identity-Lock aufgehoben");
                      window.location.reload();
                    } catch (e) {
                      toast.error(apiError(e, "Fehler beim Aufheben"));
                    }
                  }}
                  className="text-[10px] font-bold uppercase tracking-wider text-rose-700 hover:text-rose-900 underline"
                  data-testid="identity-unlock-btn"
                >
                  Admin: aufheben
                </button>
              )}
            </div>
          )}
        </section>

        <hr className="border-cyan-100" />

        {/* Versicherungsdaten — Versicherer, Adresse, Schadennummer, Deckung */}
        <section>
          <SectionLabel>{t("case_editor.sections.insurance_data")}</SectionLabel>
          <div className="mt-2 space-y-3">
            <Field label={t("case_editor.fields.insurer")} dense>
              <InsurerCombobox
                value={c.insurance?.insurer_name || ""}
                onChange={(name, ins) => {
                  setC((prev) => ({
                    ...prev,
                    insurance: {
                      ...(prev.insurance || {}),
                      insurer_name: name,
                      ...(ins ? {
                        insurer_address: `${ins.street || ""}${ins.street ? ", " : ""}${ins.postal_code || ""} ${ins.city || ""}`.trim(),
                        insurer_phone: ins.phone || "",
                        insurer_email: ins.email || "",
                      } : {}),
                    },
                  }));
                }}
                testid="insurance-insurer"
              />
            </Field>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <Field label={t("case_editor.fields.policy_number")} dense>
                <Input data-testid="insurance-policy" value={c.insurance?.policy_number || ""} onChange={(e) => setField("insurance", "policy_number", e.target.value)} placeholder="AB-12345678" />
              </Field>
              <Field label={t("case_editor.fields.claim_number")} dense>
                <Input data-testid="insurance-claim-number" value={c.insurance?.claim_number || ""} onChange={(e) => setField("insurance", "claim_number", e.target.value)} />
              </Field>
              <Field label={t("case_editor.fields.coverage_type")} dense>
                <Select value={c.insurance?.coverage_type || ""} onValueChange={(v) => setField("insurance", "coverage_type", v)}>
                  <SelectTrigger data-testid="insurance-coverage"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="haftpflicht">{t("case_editor.fields.coverage_liability")}</SelectItem>
                    <SelectItem value="teilkasko">{t("case_editor.fields.coverage_partial")}</SelectItem>
                    <SelectItem value="vollkasko">{t("case_editor.fields.coverage_full")}</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
            </div>
          </div>
        </section>

        <hr className="border-cyan-100" />

        {/* Weitere Beteiligte — ganz unten, kollabierbar (öffnet sich beim
            Klick auf "+ Beteiligte hinzufügen"). */}
        <section>
          <AdditionalPartiesBlock
            value={c.insurance?.additional_parties || []}
            onChange={(next) => setC((prev) => ({ ...prev, insurance: { ...(prev.insurance || {}), additional_parties: next } }))}
            readOnly={!canEdit}
          />
        </section>
      </div>

      <div className="rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs px-3 py-2 flex items-start gap-2">
        <CheckCircle2 className="h-3.5 w-3.5 shrink-0 mt-0.5" />
        <span>
          Versicherungsabfrage <strong>live</strong> über GDV/EasyExpert24. Voraussetzung:
          eigene Zugangsdaten unter <em>Einstellungen → API-Keys → Versicherungszugang</em>.
        </span>
      </div>

    </div>
  );

  // Hilfs-Komponente: dichte Sektion-Überschrift für den Insurance-Step
  // (wir definieren sie inline damit die Helfer-Datei sauber bleibt).
  function SectionLabel({ children }) {
    return (
      <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-cyan-700">
        {children}
      </div>
    );
  }

  // Auto-Expand sobald die VIN das erste Mal eingetragen wird.
  // Über das Ref verhindern wir, dass der User nach manuellem
  // Einklappen wieder ungewollt aufgeklappt wird.
  const vinPresent = (c.vehicle?.vin || "").trim().length > 0;
  if (vinPresent && !vinAutoExpandedRef.current && !vehicleExpanded) {
    vinAutoExpandedRef.current = true;
    Promise.resolve().then(() => setVehicleExpanded(true));
  }
  // DAT-Block bleibt standardmäßig IMMER eingeklappt (Wunsch des Users) —
  // auch wenn bereits DAT-Daten vorliegen. Der User klappt manuell auf
  // über den "DAT-Daten anzeigen"-Button. Wir behalten `hasDatData` nur
  // für die Hinweis-Karte ("DAT-Daten werden vom Backoffice abgerufen").
  const hasDatData = !!c.vehicle?.dat_data || !!c.vehicle?.make;

  const renderVehicle = () => {
    // Admin/Backoffice sehen alle Felder immer (kein Toggle).
    // Nur reguläre User (Gutachter/Franchise) bekommen das eingeklappte
    // Verhalten mit Auto-Expand nach VIN-Eingabe.
    const showFullForm = isBO ? true : vehicleExpanded;
    const isCollapsible = !isBO;
    return (
    <div className="w-full space-y-5" data-testid="step-vehicle">
      <StepHeader icon={Car} title={t("case_editor.headers.vehicle_title")} subtitle={t("case_editor.headers.vehicle_subtitle")} />

      {/* Hinweis: DAT-Lookup-Buttons sind direkt an VIN, HSN/TSN und DAT-€-Code
          (kleine Lupe rechts neben dem Feld). Nur sichtbar für Admin/Backoffice. */}
      {isBO && (
        <div className="rounded-2xl border border-cyan-200 bg-cyan-50/40 p-3 flex flex-wrap items-center gap-2 text-xs text-cyan-900" data-testid="external-apis">
          <Search className="h-3.5 w-3.5" />
          <span>{t("case_editor.actions.dat_lupe_hint", "DAT-Lupe direkt an VIN, HSN/TSN oder DAT-€-Code anklicken — Fahrzeugdaten werden automatisch geladen.")}</span>
        </div>
      )}

      {/* VIN-Block — IMMER sichtbar. Der Rest des Formulars erscheint erst,
          wenn hier etwas steht (Auto-Expand). */}
      <div className="rounded-2xl border border-zinc-200 bg-white p-4 sm:p-5" data-testid="vehicle-vin-card">
        <Field label={t("case_editor.fields.vin")} highlight={isHl("vehicle.vin")} autoFilled={isAutoFilled("vehicle.vin")}>
          <div className="flex gap-2">
            <Input className={hlClass("vehicle.vin")} data-testid="vehicle-vin" value={c.vehicle?.vin || ""} onChange={(e) => setField("vehicle", "vin", e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 17))} placeholder="WBA..." maxLength={17} />
            {isBO && (
              <Button type="button" variant="outline" size="icon" onClick={() => runDATLookup("vin")} disabled={datLoading || !(c.vehicle?.vin || "").trim()} title={t("case_editor.actions.show_vehicle_data")} data-testid="vehicle-vin-search">
                {datLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
              </Button>
            )}
          </div>
          <VINLiveInfo vin={c.vehicle?.vin || ""} />
        </Field>
        {isCollapsible && !showFullForm && (
          <div className="mt-3 text-xs text-zinc-500">
            {vinPresent
              ? t("case_editor.actions.vin_hint_filled")
              : t("case_editor.actions.vin_hint_empty")}
          </div>
        )}
        {isCollapsible && (
          <div className="mt-3">
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => setVehicleExpanded((v) => !v)}
              data-testid="vehicle-form-toggle"
              className="text-xs"
            >
              {showFullForm
                ? <><ChevronUp className="h-3.5 w-3.5 mr-1.5" /> {t("case_editor.actions.hide_vehicle_data")}</>
                : <><ChevronDown className="h-3.5 w-3.5 mr-1.5" /> {t("case_editor.actions.show_vehicle_data")}</>}
            </Button>
          </div>
        )}
      </div>

      {/* Für End-User (nicht Admin): Kennzeichen, HSN/TSN, Kilometerstand
          sind IMMER sichtbar — auch wenn der Rest des Formulars eingeklappt
          ist. Diese drei Felder sind die häufigsten Eingaben des Gutachters. */}
      {isCollapsible && (
        <div className="grid md:grid-cols-2 gap-4" data-testid="vehicle-essentials-grid">
          <Field label={t("case_editor.fields.plate")} highlight={isHl("vehicle.license_plate")} autoFilled={isAutoFilled("vehicle.license_plate")}>
            <Input className={hlClass("vehicle.license_plate")} data-testid="vehicle-plate-essential" value={c.vehicle?.license_plate || ""} onChange={(e) => setField("vehicle", "license_plate", e.target.value)} placeholder="M-XX 1234" />
            <PlateLiveInfo plate={c.vehicle?.license_plate || ""} />
          </Field>
          <Field label={t("case_editor.fields.hsn_tsn")} highlight={isHl("vehicle.hsn") || isHl("vehicle.tsn")} autoFilled={isAutoFilled("vehicle.hsn") || isAutoFilled("vehicle.tsn")}>
            <div className="flex gap-2">
              <Input className={`w-24 ${hlClass("vehicle.hsn")}`} data-testid="vehicle-hsn-essential" value={c.vehicle?.hsn || ""} onChange={(e) => setField("vehicle", "hsn", e.target.value)} placeholder="HSN" maxLength={4} />
              <Input className={`flex-1 ${hlClass("vehicle.tsn")}`} data-testid="vehicle-tsn-essential" value={c.vehicle?.tsn || ""} onChange={(e) => setField("vehicle", "tsn", e.target.value)} placeholder="TSN" maxLength={3} />
            </div>
          </Field>
          <Field label={t("case_editor.fields.mileage")}>
            <div className="flex gap-2">
              <Input type="number" data-testid="vehicle-mileage-essential" value={c.vehicle?.mileage || ""} onChange={(e) => setField("vehicle", "mileage", Number(e.target.value) || null)} placeholder="89540" />
              <Select value={c.vehicle?.mileage_source || "abgelesen"} onValueChange={(v) => setField("vehicle", "mileage_source", v)}>
                <SelectTrigger className="w-40" data-testid="vehicle-mileage-source-essential">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="abgelesen">{t("case_editor.fields.mileage_read")}</SelectItem>
                  <SelectItem value="angegeben">{t("case_editor.fields.mileage_stated")}</SelectItem>
                  <SelectItem value="geschaetzt">{t("case_editor.fields.mileage_estimated")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </Field>
        </div>
      )}

      {showFullForm && (
      <div className="grid md:grid-cols-2 gap-4" data-testid="vehicle-fields-grid">
        {/* Kennzeichen, HSN/TSN, Kilometerstand sind für End-User bereits
            oben im "essentials" Grid sichtbar. Für Admin (kein essentials
            Grid) zeigen wir sie hier im Hauptformular. */}
        {!isCollapsible && (
          <Field label={t("case_editor.fields.plate")} highlight={isHl("vehicle.license_plate")} autoFilled={isAutoFilled("vehicle.license_plate")}>
            <Input className={hlClass("vehicle.license_plate")} data-testid="vehicle-plate" value={c.vehicle?.license_plate || ""} onChange={(e) => setField("vehicle", "license_plate", e.target.value)} placeholder="M-XX 1234" />
            <PlateLiveInfo plate={c.vehicle?.license_plate || ""} />
          </Field>
        )}

        {/* HSN/TSN mit Lupe-Button — nur in Admin-Ansicht */}
        {!isCollapsible && (
          <Field label={t("case_editor.fields.hsn_tsn")} highlight={isHl("vehicle.hsn") || isHl("vehicle.tsn")} autoFilled={isAutoFilled("vehicle.hsn") || isAutoFilled("vehicle.tsn")}>
            <div className="flex gap-2">
              <Input className={`w-24 ${hlClass("vehicle.hsn")}`} data-testid="vehicle-hsn" value={c.vehicle?.hsn || ""} onChange={(e) => setField("vehicle", "hsn", e.target.value)} placeholder="HSN" maxLength={4} />
              <Input className={`w-32 ${hlClass("vehicle.tsn")}`} data-testid="vehicle-tsn" value={c.vehicle?.tsn || ""} onChange={(e) => setField("vehicle", "tsn", e.target.value)} placeholder="TSN" maxLength={3} />
              {isBO && (
                <Button type="button" variant="outline" size="icon" onClick={() => runDATLookup("kba")} disabled={datLoading || !((c.vehicle?.hsn || "").trim() && (c.vehicle?.tsn || "").trim())} title="HSN/TSN via DAT" data-testid="vehicle-hsn-tsn-search">
                  {datLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                </Button>
              )}
            </div>
          </Field>
        )}

        {/* DAT-€-Code + alle abgeleiteten Felder (Hersteller, Modell,
            Erstzulassung, Baujahr, Farbe, Kraftstoff, Leistung, HU/AU) sind
            für End-User über ein eigenes Toggle ein-/ausblendbar.
            Auto-Expand beim ersten Eintreffen von DAT-Daten. Admins sehen
            sie immer (sie lösen die Abfrage selbst aus). */}
        {isCollapsible && (
          <div className="md:col-span-2 flex items-center justify-between gap-3 mb-1" data-testid="vehicle-dat-toggle-row">
            <div className="text-xs text-zinc-500">
              <strong className="text-zinc-700">{t("case_editor.dat.label")}</strong>{" "}
              {hasDatData
                ? t("case_editor.dat.data_available")
                : t("case_editor.dat.data_pending")}
            </div>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => setDatBlockExpanded((v) => !v)}
              data-testid="vehicle-dat-toggle"
              className="text-xs"
            >
              {datBlockExpanded
                ? <><ChevronUp className="h-3.5 w-3.5 mr-1.5" /> {t("case_editor.actions.hide_dat_data")}</>
                : <><ChevronDown className="h-3.5 w-3.5 mr-1.5" /> {t("case_editor.actions.show_dat_data")}</>}
            </Button>
          </div>
        )}
        {(isBO || datBlockExpanded) && (
          <>
            <Field label={t("case_editor.fields.dat_eur_code")}>
              <div className="flex gap-2">
                <Input data-testid="vehicle-dat-eur-code" value={c.vehicle?.dat_eur_code || ""} onChange={(e) => setField("vehicle", "dat_eur_code", e.target.value)} placeholder="z. B. ABCDE-12345" />
                {isBO && (
                  <Button type="button" variant="outline" size="icon" onClick={() => runDATLookup("europa")} disabled={datLoading || !(c.vehicle?.dat_eur_code || "").trim()} title="DAT-€-Code via DAT" data-testid="vehicle-dat-eur-code-pick">
                    {datLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                  </Button>
                )}
              </div>
            </Field>

            <Field label={t("case_editor.fields.make")} highlight={isHl("vehicle.make")} autoFilled={isAutoFilled("vehicle.make")}>
              <Input className={hlClass("vehicle.make")} data-testid="vehicle-make" value={c.vehicle?.make || ""} onChange={(e) => setField("vehicle", "make", e.target.value)} />
            </Field>
            <Field label={t("case_editor.fields.model")} highlight={isHl("vehicle.model")} autoFilled={isAutoFilled("vehicle.model")}>
              <Input className={hlClass("vehicle.model")} data-testid="vehicle-model" value={c.vehicle?.model || ""} onChange={(e) => setField("vehicle", "model", e.target.value)} />
            </Field>
            <Field label={t("case_editor.fields.first_registration")} highlight={isHl("vehicle.first_registration")} autoFilled={isAutoFilled("vehicle.first_registration")}>
              <Input className={hlClass("vehicle.first_registration")} data-testid="vehicle-first-reg" value={c.vehicle?.first_registration || ""} onChange={(e) => setField("vehicle", "first_registration", e.target.value)} placeholder="TT.MM.JJJJ" />
            </Field>
            <Field label={t("case_editor.fields.year")} highlight={isHl("vehicle.year")} autoFilled={isAutoFilled("vehicle.year")}>
              <Input className={hlClass("vehicle.year")} type="number" data-testid="vehicle-year" value={c.vehicle?.year || ""} onChange={(e) => setField("vehicle", "year", Number(e.target.value) || null)} />
            </Field>
            <Field label={t("case_editor.fields.color")} highlight={isHl("vehicle.color")} autoFilled={isAutoFilled("vehicle.color")}>
              <Input className={hlClass("vehicle.color")} data-testid="vehicle-color" value={c.vehicle?.color || ""} onChange={(e) => setField("vehicle", "color", e.target.value)} />
            </Field>
            <Field label={t("case_editor.fields.fuel_type")} highlight={isHl("vehicle.fuel_type")} autoFilled={isAutoFilled("vehicle.fuel_type")}>
              <Input className={hlClass("vehicle.fuel_type")} data-testid="vehicle-fuel" value={c.vehicle?.fuel_type || ""} onChange={(e) => setField("vehicle", "fuel_type", e.target.value)} />
            </Field>
            <Field label={t("case_editor.fields.power_kw")} highlight={isHl("vehicle.power_kw")} autoFilled={isAutoFilled("vehicle.power_kw")}>
              <Input className={hlClass("vehicle.power_kw")} type="number" data-testid="vehicle-power" value={c.vehicle?.power_kw || ""} onChange={(e) => setField("vehicle", "power_kw", Number(e.target.value) || null)} />
            </Field>
            <Field label={t("case_editor.fields.next_hu")} highlight={isHl("vehicle.next_hu")} autoFilled={isAutoFilled("vehicle.next_hu")}>
              <Input className={hlClass("vehicle.next_hu")} data-testid="vehicle-next-hu" value={c.vehicle?.next_hu || ""} onChange={(e) => setField("vehicle", "next_hu", e.target.value)} placeholder="MM/JJJJ" />
            </Field>
          </>
        )}

        {/* Hinweis-Karte für End-User, wenn DAT-Daten noch nicht vorliegen
            UND der DAT-Block kollabiert ist. */}
        {isCollapsible && !hasDatData && !datBlockExpanded && (
          <div className="md:col-span-2 rounded-xl border border-dashed border-amber-200 bg-amber-50/40 p-4 text-xs text-amber-800 flex items-start gap-2" data-testid="dat-pending-hint">
            <Search className="h-4 w-4 mt-0.5 shrink-0" />
            <div>
              {t("case_editor.dat.pending_hint_p1")}
              <strong>{t("case_editor.dat.pending_hint_strong")}</strong>
              {t("case_editor.dat.pending_hint_p2")}
            </div>
          </div>
        )}

        {/* Kilometerstand + Quelle — nur in Admin-Ansicht hier; bei Usern
            ist es bereits oben im essentials Grid sichtbar. */}
        {!isCollapsible && (
          <Field label={t("case_editor.fields.mileage")}>
            <div className="flex gap-2">
              <Input type="number" data-testid="vehicle-mileage" value={c.vehicle?.mileage || ""} onChange={(e) => setField("vehicle", "mileage", Number(e.target.value) || null)} placeholder="89540" />
              <Select value={c.vehicle?.mileage_source || "abgelesen"} onValueChange={(v) => setField("vehicle", "mileage_source", v)}>
                <SelectTrigger className="w-40" data-testid="vehicle-mileage-source">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="abgelesen">{t("case_editor.fields.mileage_read")}</SelectItem>
                  <SelectItem value="angegeben">{t("case_editor.fields.mileage_stated")}</SelectItem>
                  <SelectItem value="geschaetzt">{t("case_editor.fields.mileage_estimated")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </Field>
        )}
      </div>
      )}

      {/* Reiter "Ausstattung & Bereifung" — hinter Toggle, für User auch
          hinter dem DAT-Block-Toggle (oben). */}
      {showFullForm && (isBO || datBlockExpanded) && (
      <div className="rounded-2xl border border-zinc-200 bg-white overflow-hidden" data-testid="vehicle-equipment-block">
        <div className="px-4 py-3 border-b border-zinc-200 bg-zinc-50/60 flex items-center gap-3">
          <div className="h-8 w-8 rounded-lg brand-gradient-soft flex items-center justify-center">
            <Sparkle className="h-4 w-4 text-cyan-700" />
          </div>
          <div className="font-display font-semibold text-sm">{t("case_editor.sections.equipment_tires")}</div>
        </div>
        <div className="p-4 space-y-4">
          <Field label={t("case_editor.fields.equipment")}>
            <Textarea
              rows={3}
              data-testid="vehicle-equipment"
              value={c.vehicle?.equipment || ""}
              onChange={(e) => setField("vehicle", "equipment", e.target.value)}
              placeholder="z. B. Lederausstattung, Standheizung, AHK, Navigationssystem, Xenon-Licht…"
            />
          </Field>
          <Field label={t("case_editor.fields.tires")}>
            <Textarea
              rows={3}
              data-testid="vehicle-tires"
              value={c.vehicle?.tires || ""}
              onChange={(e) => setField("vehicle", "tires", e.target.value)}
              placeholder="z. B. vorne Michelin Primacy 4, 6 mm Sommer · hinten 5 mm · 17 Zoll Alu"
            />
          </Field>
        </div>
      </div>
      )}

      {showFullForm && (isBO || datBlockExpanded) && c.vehicle?.dat_data && (
        <div className="rounded-xl border border-[#12A9E8]/30 bg-[#12A9E8]/10 p-4">
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#12A9E8] mb-2">{t("case_editor.dat.valuation_mock")}</div>
          <div className="grid grid-cols-3 gap-4 text-sm">
            <div><div className="text-zinc-500 text-xs">{t("case_editor.dat.market_value")}</div><div className="font-semibold">{c.vehicle.dat_data.market_value_eur?.toLocaleString("de")} €</div></div>
            <div><div className="text-zinc-500 text-xs">{t("case_editor.dat.retail_value")}</div><div className="font-semibold">{c.vehicle.dat_data.retail_value_eur?.toLocaleString("de")} €</div></div>
            <div><div className="text-zinc-500 text-xs">{t("case_editor.dat.trade_in_value")}</div><div className="font-semibold">{c.vehicle.dat_data.trade_in_value_eur?.toLocaleString("de")} €</div></div>
          </div>
        </div>
      )}
    </div>
    );
  };

  const renderInspection = () => {
    const setInsp = (k, v) => setC((prev) => ({ ...prev, inspection: { ...(prev.inspection || {}), [k]: v } }));
    const insp = c.inspection || {};
    // Vorbelegung: Gutachter-Adresse aus User-Profil (address_line, postal_code, city)
    const ownerAddress = insp.gutachter_address || [
      user?.address_line, [user?.postal_code, user?.city].filter(Boolean).join(" ")
    ].filter(Boolean).join(", ");
    const customerAddress = insp.inspection_location || [
      c.customer?.address, [c.customer?.postal_code, c.customer?.city].filter(Boolean).join(" ")
    ].filter(Boolean).join(", ");
    return (
      <div className="w-full space-y-5" data-testid="step-inspection">
        <StepHeader icon={MapPin} title={t("case_editor.headers.inspection_title")} subtitle={t("case_editor.headers.inspection_subtitle")} />

        <div className="rounded-2xl border border-cyan-100 bg-gradient-to-br from-cyan-50/40 to-white p-4 sm:p-5 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label={t("case_editor.fields.inspection_location")} dense>
              <Input
                data-testid="insp-location"
                value={insp.inspection_location || ""}
                onChange={(e) => setInsp("inspection_location", e.target.value)}
                placeholder="z. B. Musterstr. 12, 50667 Köln"
              />
            </Field>
            <Field label={t("case_editor.fields.inspection_date")} dense>
              <Input
                type="date"
                data-testid="insp-date"
                value={insp.inspection_date || ""}
                onChange={(e) => setInsp("inspection_date", e.target.value)}
              />
            </Field>
            <Field label={t("case_editor.fields.gutachter")} dense>
              <Input
                data-testid="insp-gutachter"
                value={insp.gutachter_name || c.owner?.full_name || ""}
                onChange={(e) => setInsp("gutachter_name", e.target.value)}
                placeholder="Name des Sachverständigen"
              />
            </Field>
            <Field label={t("case_editor.fields.distance_km")} dense>
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  step="0.1"
                  min="0"
                  data-testid="insp-distance-km"
                  value={insp.distance_km ?? ""}
                  onChange={(e) => {
                    const v = e.target.value;
                    const km = v === "" ? null : Number(v);
                    setInsp("distance_km", km);
                    if (km !== null && !Number.isNaN(km)) {
                      setC((prev) => ({
                        ...prev,
                        honorar: { ...(prev.honorar || {}), travel_km: km },
                      }));
                    }
                  }}
                  placeholder="wird per Maps berechnet"
                />
                <span className="text-xs text-zinc-500 font-medium shrink-0">km</span>
              </div>
            </Field>
            <Field label="Fahrbereitschaft" dense>
              <Select
                value={insp.driveable_state || "Verkehrssicher"}
                onValueChange={(v) => setInsp("driveable_state", v)}
              >
                <SelectTrigger data-testid="insp-driveable-state">
                  <SelectValue placeholder="Fahrbereitschaft wählen" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Verkehrssicher">Verkehrssicher</SelectItem>
                  <SelectItem value="Bedingt verkehrssicher">Bedingt verkehrssicher</SelectItem>
                  <SelectItem value="Nicht verkehrssicher">Nicht verkehrssicher</SelectItem>
                </SelectContent>
              </Select>
            </Field>
          </div>

          <InspectionMapsBlock
            gutachterAddress={ownerAddress}
            customerAddress={customerAddress}
            onGutachterAddressChange={(v) => setInsp("gutachter_address", v)}
            onCustomerAddressChange={(v) => setInsp("inspection_location", v)}
            onDistanceCalculated={(km, durationMin) => {
              setInsp("distance_km", km);
              setInsp("travel_duration_min", durationMin);
              setC((prev) => ({
                ...prev,
                honorar: { ...(prev.honorar || {}), travel_km: km },
              }));
            }}
          />

          <div className="text-[11px] text-zinc-500 leading-snug bg-zinc-50 border border-zinc-200 rounded-lg px-3 py-2">
            💡 {t("case_editor.dat.distance_km_hint")}
          </div>
        </div>
      </div>
    );
  };

  const renderCalculation = () => (
    <div className="space-y-6">
      {isBO ? (
        calcClassicView ? (
          <>
            {/* ── Klassische DAT-Ansicht (Fallback) ── */}
            <div className="flex items-center justify-between p-3 bg-gradient-to-r from-zinc-50 to-zinc-100 border border-zinc-200 rounded-xl">
              <div className="text-xs text-zinc-600">
                <strong className="text-zinc-900">Klassische Ansicht aktiv</strong> — die neue Kalkulation mit Auto-Übernahme ist einen Klick entfernt.
              </div>
              <Button variant="outline" size="sm" onClick={() => setCalcView(false)} data-testid="calc-switch-new-btn">
                <Sparkles className="h-3.5 w-3.5 mr-1.5" /> Neue Ansicht
              </Button>
            </div>

            {/* DAT-PDF-Scan-Fallback — wenn die API-Iframe-Variante mal nicht klappt */}
            <div className="flex items-center justify-between p-3 bg-gradient-to-r from-cyan-50 to-blue-50 border border-cyan-200 rounded-xl">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-white rounded-lg">
                  <Sparkles className="h-5 w-5 text-cyan-600" />
                </div>
                <div>
                  <div className="text-sm font-semibold text-zinc-900">{t("case_editor.actions.scan_pdf_title")}</div>
                  <div className="text-[11px] text-zinc-600 mt-0.5">{t("case_editor.actions.scan_pdf_subtitle")}</div>
                </div>
              </div>
              <Button
                variant="outline"
                className="border-cyan-300 text-cyan-700 hover:bg-cyan-100"
                onClick={() => { setDatScanInitialFile(null); setDatScanOpen(true); }}
                data-testid="dat-pdf-scan-open-btn"
              >
                <FileText className="h-4 w-4 mr-2" /> {t("case_editor.scan_pdf")}
              </Button>
            </div>

            {/* Lichtbilder-Slider — 3 Bilder sichtbar, scrollen + Klick→Lightbox. */}
            <CalculationPhotoSlider photos={c.photos || []} />

            <DATCalculateExpert caseId={c.id} vehicle={c.vehicle || {}} />
          </>
        ) : (
          <>
            {/* ── Neue, aufgeräumte Kalkulation (Default) ── */}
            <CalculationPhotoSlider photos={c.photos || []} />
            <CalculationStreamlined
              caseId={c.id}
              onOpenDatScan={() => { setDatScanInitialFile(null); setDatScanOpen(true); }}
              onSwitchToClassic={() => setCalcView(true)}
            />
          </>
        )
      ) : (
        <CalculationStep
          value={c.calculation || {}}
          onChange={(next) => setC((prev) => ({ ...prev, calculation: next }))}
          onSave={() => saveCase()}
          saving={saving}
          readOnly={!canEdit}
        />
      )}
      <DATPdfScanModal
        caseId={c.id}
        open={datScanOpen}
        initialFile={datScanInitialFile}
        onClose={() => { setDatScanOpen(false); setDatScanInitialFile(null); }}
        onApplied={(patch) => {
          setC((prev) => ({
            ...prev,
            ...(patch.vehicle ? { vehicle: { ...(prev.vehicle || {}), ...patch.vehicle } } : {}),
            ...(patch.calculation ? { calculation: { ...(prev.calculation || {}), ...patch.calculation } } : {}),
          }));
        }}
      />
    </div>
  );

  const renderValuation = () => (
    <div className="space-y-4" data-testid="step-valuation-wrap">
      {/* DAT valuateExpert Iframe — Admin-only (analog zu calculateExpert) */}
      {isBO && (
        <DATValuateExpert
          caseId={id}
          vehicle={c.vehicle || {}}
          onValuationChanged={(val) => {
            // Aktualisiere die UI-Werte direkt nach Pull-Back (server hat
            // die Akte bereits persistiert; wir patchen die State-Kopie).
            if (!val || Object.keys(val).length === 0) return;
            setC((prev) => ({
              ...prev,
              valuation: {
                ...(prev.valuation || {}),
                replacement_value: val.replacement_value || prev.valuation?.replacement_value,
                residual_value: val.restwert || prev.valuation?.residual_value,
                dat_valuation: val,
              },
            }));
          }}
        />
      )}
      <ValuationStep
        caseId={c.id}
        vehicle={c.vehicle || {}}
        accountingValue={c.accounting || {}}
        value={c.valuation || {}}
        calculation={c.calculation || {}}
        ausfallValue={c.ausfall || {}}
        onChange={(next) => setC((prev) => ({ ...prev, valuation: next }))}
        onAusfallChange={(next) => setC((prev) => ({ ...prev, ausfall: next }))}
        onAccountingChange={(next) => setC((prev) => ({ ...prev, accounting: next }))}
        onSave={() => saveCase()}
        saving={saving}
        readOnly={!canEdit}
      />
    </div>
  );

  const renderHonorar = () => (
    <HonorarStep
      value={c.honorar || {}}
      calculation={c.calculation || {}}
      valuation={c.valuation || {}}
      photoCount={(c.photos || []).length}
      inspectionDistanceKm={c.inspection?.distance_km || c.customer?.distance_km || null}
      caseId={c.id}
      onChange={(next) => setC((prev) => ({ ...prev, honorar: next }))}
      onSave={() => saveCase()}
      saving={saving}
      readOnly={!canEdit}
    />
  );

  const renderBegutachtung = () => (
    <BegutachtungStep
      value={c.begutachtung || {}}
      onChange={(next) => setC((prev) => ({ ...prev, begutachtung: next }))}
      onSave={() => saveCase()}
      saving={saving}
      readOnly={!canEdit}
      caseId={c.id}
      vehicle={c.vehicle || {}}
      customer={c.customer || {}}
      preDamages={c.pre_damages || {}}
      preDamagesArrows={c.pre_damages_arrows || []}
      onPreDamagesChange={(next) => setC((prev) => ({ ...prev, pre_damages: next }))}
      onPreDamagesArrowsChange={(arrows) => setC((prev) => ({ ...prev, pre_damages_arrows: arrows }))}
    />
  );

  const renderPhotos = () => {
    const photoCount = (c.photos || []).length;
    const backendBase = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
    const zipHref = id && photoCount > 0
      ? `${backendBase}/api/files/cases/${id}/photos.zip?auth=${encodeURIComponent(localStorage.getItem("access_token") || "")}`
      : null;

    return (
      <div className="w-full space-y-6" data-testid="step-photos">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <StepHeader icon={Camera} title={t("case_editor.headers.photos_title")} subtitle={t("case_editor.headers.photos_subtitle")} />
          </div>
          {/* Desktop-only: kompakter Auslöser. Mobile-User starten die Kamera
              direkt über den Foto-Button in der Bottom-Navigation. */}
          <button
            type="button"
            onClick={() => setCameraTrigger((c) => c + 1)}
            disabled={!id}
            className="hidden sm:inline-flex shrink-0 items-center gap-2 brand-gradient-bg text-white rounded-xl px-4 h-11 text-sm font-semibold shadow-md hover:shadow-[0_0_20px_rgba(8,145,178,0.4)] active:scale-95 transition disabled:opacity-50 mt-1"
            data-testid="photos-open-camera-btn"
            title={t("case_editor.take_photos_now")}
          >
            <Camera className="h-5 w-5" strokeWidth={1.9} />
            {t("case_editor.take_photos_now")}
          </button>
        </div>

        {/* OBEN: Upload-Tile + bereits aufgenommene Fotos. So sieht der Nutzer
            sofort, was er bisher hat — und der Drag-&-Drop-Bereich ist als
            Erstes zur Hand. */}
        <PhotoUploader caseId={id} photos={c.photos || []} onChange={(photos) => setC((prev) => ({ ...prev, photos }))} />

        {/* Admin/Backoffice: ZIP-Download aller Lichtbilder dieser Akte. */}
        {isBO && photoCount > 0 && (
          <div className="rounded-xl border border-zinc-200 bg-zinc-50/60 px-4 py-3 flex items-center justify-between gap-3" data-testid="photos-admin-zip-block">
            <div className="flex items-center gap-3 min-w-0">
              <div className="h-9 w-9 rounded-lg bg-cyan-100 text-cyan-700 flex items-center justify-center shrink-0">
                <FileDown className="h-4 w-4" strokeWidth={1.9} />
              </div>
              <div className="text-xs text-zinc-600 min-w-0">
                <div className="font-semibold text-zinc-900 text-sm">{t("case_editor.photos.zip_title")}</div>
                <div className="text-[11px] text-zinc-500 mt-0.5 truncate">
                  {t("case_editor.photos.photo_count", { count: photoCount })}
                </div>
              </div>
            </div>
            <a
              href={zipHref || "#"}
              download
              className="inline-flex items-center gap-2 brand-gradient-bg text-white rounded-lg px-3.5 h-9 text-xs font-semibold shadow-sm hover:shadow-[0_0_18px_rgba(8,145,178,0.35)] active:scale-95 transition shrink-0"
              data-testid="photos-download-zip-btn"
              title={t("case_editor.download_zip")}
            >
              <Download className="h-3.5 w-3.5" strokeWidth={2} />
              {t("case_editor.download_zip")}
            </a>
          </div>
        )}

        {/* DARUNTER: Beispielfotos als schlichte Checkliste. */}
        <div className="pt-4 border-t border-zinc-200">
          <PhotoGuide
            caseId={id}
            photos={c.photos || []}
            onChange={(photos) => setC((prev) => ({ ...prev, photos }))}
            cameraTrigger={cameraTrigger}
          />
        </div>
      </div>
    );
  };

  const renderSignature = () => (
    <div className="w-full space-y-5" data-testid="step-signature">
      <StepHeader icon={PenLine} title={t("case_editor.headers.signature_title")} subtitle={t("case_editor.headers.signature_subtitle")} />
      <Tabs defaultValue="abtretung" className="w-full">
        <TabsList data-testid="signature-tabs">
          <TabsTrigger value="abtretung" data-testid="tab-abtretung">Abtretung</TabsTrigger>
          <TabsTrigger value="vollmacht" data-testid="tab-vollmacht">Vollmacht Anwalt</TabsTrigger>
        </TabsList>

        <TabsContent value="abtretung" className="space-y-5 mt-4">
          <div className="flex items-center justify-between gap-3 rounded-xl border border-zinc-200 bg-zinc-50/60 px-4 py-2.5">
            <div className="flex items-center gap-2 text-xs text-zinc-600">
              <FileText className="h-3.5 w-3.5 text-zinc-500" />
              <span><strong className="text-zinc-800">{t("case_editor.signature_block.title")}</strong> · {t("case_editor.signature_block.subtitle")}</span>
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setShowAbtretung((v) => !v)}
              data-testid="abtretung-toggle"
            >
              {showAbtretung ? <><Eye className="h-3.5 w-3.5 mr-1.5" /> {t("case_editor.hide")}</> : <><Eye className="h-3.5 w-3.5 mr-1.5" /> {t("case_editor.show")}</>}
            </Button>
          </div>
          {showAbtretung && (
            <AbtretungsErklaerung
              c={c}
              user={user}
              accessToken={accessToken}
              gutachterName={user?.company || user?.full_name || ""}
              onClauseChange={(next) => setC((prev) => ({
                ...prev,
                abtretung: { ...(prev.abtretung || {}), clauses: next },
              }))}
              readOnly={!canEdit}
            />
          )}
          <div className="rounded-2xl border border-zinc-200 bg-white p-5">
            <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-zinc-500 mb-3">
              {t("case_editor.sections.signature_damaged")}
            </div>
            <SignatureCanvas
              caseId={id}
              existing={c.signature}
              defaultSignerName={[c.customer?.firstname, c.customer?.name].filter(Boolean).join(" ").trim()}
              onChange={(sig) => setC((prev) => ({ ...prev, signature: sig }))}
              hideSignerInput
            />
          </div>
        </TabsContent>

        <TabsContent value="vollmacht" className="mt-4">
          <AnwaltsVollmacht
            c={c}
            user={user}
            readOnly={!canEdit}
            onSaved={(next) => setC((prev) => ({ ...prev, anwalt_vollmacht: next }))}
          />
        </TabsContent>
      </Tabs>
    </div>
  );

  const renderDocuments = () => <DocumentsStep c={c} onChange={(docs) => setC((prev) => ({ ...prev, documents: docs }))} />;

  const renderPreDamages = () => (
    <div className="w-full space-y-5" data-testid="step-predamages">
      <StepHeader icon={History} title={t("case_editor.headers.predamages_title")} subtitle={t("case_editor.headers.predamages_subtitle")} />
      <VehicleDamageMap
        value={c.pre_damages || {}}
        arrows={c.pre_damages_arrows || []}
        onChange={(next) => setC((prev) => ({ ...prev, pre_damages: next }))}
        onArrowsChange={(arrows) => setC((prev) => ({ ...prev, pre_damages_arrows: arrows }))}
        readOnly={!canEdit}
      />
      <div className="flex gap-3">
        <Button
          variant="outline"
          onClick={() => setC((prev) => ({ ...prev, pre_damages: {} }))}
          disabled={!canEdit || !Object.keys(c.pre_damages || {}).length}
          data-testid="predamages-reset-btn"
        >
          <Trash2 className="h-4 w-4 mr-2" /> {t("case_editor.reset_all")}
        </Button>
      </div>
    </div>
  );

  const renderReview = () => (
    <div className="w-full space-y-5" data-testid="step-review">
      <StepHeader icon={CheckCircle2} title={t("case_editor.headers.review_title")} subtitle={t("case_editor.headers.review_subtitle")} />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {visibleSteps.filter((s) => s.key !== "review").map((s) => (          <div key={s.key} className={cn(
            "rounded-xl border p-3 text-sm",
            completeness[s.key] ? "border-emerald-200 bg-emerald-50/50" : "border-zinc-200 bg-zinc-50",
          )}>
            <div className="flex items-center justify-between">
              <span className="font-medium">{s.label}</span>
              {completeness[s.key] ? <CheckCircle2 className="h-4 w-4 text-[#12A9E8]" /> : <AlertCircle className="h-4 w-4 text-zinc-400" />}
            </div>
          </div>
        ))}
      </div>

      <div className="space-y-3">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <Label htmlFor="damage" className="text-base font-semibold">{t("case_editor.fields.damage_description")}</Label>
          <VoiceRecorder compact onTranscript={(tx) => setC((prev) => ({ ...prev, damage_description: (prev.damage_description ? prev.damage_description + "\n\n" : "") + tx }))} />
        </div>

        {/* Quick-template dropdown — auto-fills the textarea with a German legal-style draft. */}
        <div className="rounded-lg border border-zinc-200 bg-zinc-50/50 p-3 flex flex-wrap items-center gap-3" data-testid="accident-template-bar">
          <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-zinc-500">
            {t("case_editor.review.insert_template")}
          </div>
          <Select
            onValueChange={(key) => {
              if (!key) return;
              const text = buildAccidentText(key, c);
              setC((prev) => {
                // Append (don't overwrite) so the user can build descriptions from
                // multiple templates without losing prior input.
                const sep = prev.damage_description?.trim() ? "\n\n" : "";
                return { ...prev, damage_description: (prev.damage_description || "") + sep + text };
              });
            }}
          >
            <SelectTrigger className="w-full sm:w-[420px] bg-white" data-testid="accident-template-select">
              <SelectValue placeholder={t("case_editor.review.template_placeholder")} />
            </SelectTrigger>
            <SelectContent>
              {ACCIDENT_TEMPLATES.map((tpl) => (
                <SelectItem key={tpl.key} value={tpl.key} data-testid={`accident-template-${tpl.key}`}>
                  {tpl.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <span className="text-[11px] text-zinc-500">
            {t("case_editor.review.template_hint")}
          </span>
        </div>

        <Textarea
          id="damage"
          value={c.damage_description || ""}
          onChange={(e) => setC((prev) => ({ ...prev, damage_description: e.target.value }))}
          rows={10}
          placeholder={t("case_editor.review.textarea_placeholder")}
          data-testid="review-damage-textarea"
        />
      </div>

      {isBO && (
        <div className="ai-border p-5 space-y-3" data-testid="ai-report-panel">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl brand-gradient-bg flex items-center justify-center">
              <Sparkles className="h-5 w-5 text-white" />
            </div>
            <div className="flex-1">
              <div className="font-display font-semibold text-lg">{t("case_editor.review.ai_title")}</div>
              <div className="text-xs text-zinc-500">{t("case_editor.review.ai_subtitle")}</div>
            </div>
            <Button
              onClick={generateReport}
              disabled={generating}
              className="brand-gradient-bg text-white"
              data-testid="ai-generate-report-btn"
            >
              {generating ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkle className="h-4 w-4 mr-2" />}
              {c.ai_generated_report ? t("case_editor.regenerate_text") : t("case_editor.generate_text")}
            </Button>
          </div>
          {c.ai_generated_report && (
            <Textarea
              value={c.ai_generated_report}
              onChange={(e) => setC((prev) => ({ ...prev, ai_generated_report: e.target.value }))}
              rows={10}
              className="font-mono text-[13px]"
              data-testid="ai-report-textarea"
            />
          )}
        </div>
      )}

      {isBO && (
        <>
          {/* Senden-Hero: prominenter Block, der den Workflow-Abschluss klar
              kommuniziert. Erscheint NUR solange die Akte noch nicht
              versendet wurde (status !== ready_for_gutachter & released ...). */}
          {!["ready_for_gutachter", "released", "sent_to_customer", "completed", "closed"].includes(c.status) && (
            <div
              className="rounded-2xl border-2 border-[#12A9E8]/30 bg-gradient-to-br from-[#12A9E8]/8 via-[#12A9E8]/5 to-[#8B5CF6]/8 p-5 mt-4"
              data-testid="review-send-hero"
            >
              <div className="flex items-start gap-4 flex-wrap">
                <div className="h-12 w-12 rounded-xl brand-gradient-bg flex items-center justify-center shrink-0 shadow-md">
                  <Send className="h-5 w-5 text-white" />
                </div>
                <div className="flex-1 min-w-[220px]">
                  <div className="font-display font-bold text-zinc-900 text-base">
                    {t("case_editor.review.send_hero_title")}
                  </div>
                  <div className="text-xs text-zinc-600 mt-1 leading-relaxed">
                    {t("case_editor.review.send_hero_subtitle")}
                  </div>
                </div>
                <Button
                  onClick={finalizeCase}
                  disabled={saving}
                  size="lg"
                  className="brand-gradient-bg hover:opacity-90 text-white shadow-md hover:shadow-lg"
                  data-testid="review-send-hero-btn"
                >
                  {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
                  {t("case_editor.send_now")}
                </Button>
              </div>
            </div>
          )}

          {/* Bestätigung NACH dem Senden — zeigt den aktuellen Status */}
          {["ready_for_gutachter", "released", "sent_to_customer"].includes(c.status) && (
            <div
              className="rounded-2xl border-2 border-[#12A9E8]/40 bg-[#12A9E8]/10 p-5 mt-4 flex items-center gap-4 flex-wrap"
              data-testid="review-sent-confirmation"
            >
              <div className="h-10 w-10 rounded-full bg-[#12A9E8] flex items-center justify-center shrink-0">
                <CheckCircle2 className="h-5 w-5 text-white" />
              </div>
              <div className="flex-1 min-w-[200px]">
                <div className="font-display font-bold text-[#0E6FA0]">
                  {t("case_editor.review.sent_to_gutachter")}
                </div>
                <div className="text-xs text-[#0E6FA0]/80 mt-0.5">
                  {t("case_editor.review.version_in_inbox", { version: c.final_reports?.length || 1 })}
                  {c.status === "released" && t("case_editor.review.released_suffix")}
                  {c.status === "sent_to_customer" && t("case_editor.review.forwarded_suffix")}
                </div>
              </div>
              <Button
                onClick={finalizeCase}
                variant="outline"
                disabled={saving}
                className="border-[#12A9E8]/40 text-[#12A9E8] hover:bg-[#12A9E8]/20"
                data-testid="review-resend-btn"
              >
                {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
                {t("case_editor.resend")}
              </Button>
            </div>
          )}

        <div className="rounded-2xl border border-zinc-200 bg-zinc-50/40 p-4 mt-4" data-testid="review-actions-bar">
          <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-zinc-500 mb-3">{t("case_editor.sections.admin_actions")}</div>
          <div className="flex flex-wrap items-stretch gap-2.5">
            <Button
              onClick={() => saveCase()}
              disabled={saving}
              className="brand-gradient-bg text-white flex-1 sm:flex-none min-w-[160px]"
              data-testid="review-save-btn"
            >
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
              {t("case_editor.save")}
            </Button>
            <Button
              onClick={() => setStep("calculation")}
              className="bg-amber-500 hover:bg-amber-600 text-white flex-1 sm:flex-none min-w-[180px]"
              data-testid="review-calculate-btn"
              title={t("case_editor.calculate_now")}
            >
              <Calculator className="h-4 w-4 mr-2" />
              {t("case_editor.calculate_now")}
            </Button>
            <Button
              variant="outline"
              onClick={downloadPDF}
              className="flex-1 sm:flex-none min-w-[160px]"
              data-testid="review-pdf-btn"
            >
              <Download className="h-4 w-4 mr-2" /> {t("case_editor.pdf_preview")}
            </Button>
            <Button
              onClick={finalizeCase}
              disabled={saving}
              className="brand-gradient-bg hover:opacity-90 text-white shadow-md hover:shadow-lg transition-shadow flex-1 sm:flex-none min-w-[240px] ring-2 ring-[#12A9E8]/30 ring-offset-2"
              data-testid="review-finalize-btn"
              title={t("case_editor.send_to_gutachter")}
            >
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
              {t("case_editor.send_to_gutachter_arrow")}
            </Button>
            <Button
              variant="outline"
              onClick={() => setStatus("closed")}
              className="text-zinc-700 border-zinc-300 hover:bg-zinc-50 flex-1 sm:flex-none min-w-[140px]"
              data-testid="review-close-btn"
            >
              {t("case_editor.close_case")}
            </Button>
          </div>
        </div>
        </>
      )}
    </div>
  );

  const renderStep = () => {
    switch (step) {
      case "overview": return <OverviewStep caseData={c} onJump={(k) => setStep(k)} />;
      case "customer": return renderCustomer();
      case "insurance": return renderInsurance();
      case "vehicle": return renderVehicle();
      case "begutachtung": return renderBegutachtung();
      case "inspection": return renderInspection();
      case "calculation": return renderCalculation();
      case "valuation": return renderValuation();
      case "honorar": return renderHonorar();
      case "photos": return renderPhotos();
      case "signature": return renderSignature();
      case "predamages": return renderPreDamages();
      case "gutachten":
        return (
          <div className="space-y-4" data-testid="step-gutachten">
            <GutachtenStep caseObj={c} />
          </div>
        );
      case "documentation":
        return (
          <div className="space-y-4" data-testid="step-documentation">
            <DocumentationStep
              c={c}
              accessToken={accessToken}
              onSaved={(patch) => {
                // Wenn der Child ein patch-Objekt liefert (z.B. nach DAT-Scan),
                // mergen wir die neuen Felder in die lokale Case-Kopie, damit
                // die Doku sofort die neuen Werte zeigt.
                if (patch && typeof patch === "object") {
                  setC((prev) => ({
                    ...prev,
                    ...(patch.vehicle ? { vehicle: { ...(prev.vehicle || {}), ...patch.vehicle } } : {}),
                    ...(patch.calculation ? { calculation: { ...(prev.calculation || {}), ...patch.calculation } } : {}),
                    ...(patch.documentation ? { documentation: patch.documentation } : {}),
                    ...(patch.documents ? { documents: patch.documents } : {}),
                  }));
                }
              }}
            />
          </div>
        );
      case "review":
        return (
          <div className="space-y-8">
            {/* Banner: Korrektur angefragt — sehr prominent für Backoffice/Admin */}
            {isBO && c.status === "revision_requested" && c.revision_note && (
              <div className="rounded-2xl border-2 border-rose-200 bg-rose-50/60 p-4 flex items-start gap-3" data-testid="revision-banner">
                <div className="h-9 w-9 rounded-lg bg-rose-100 flex items-center justify-center shrink-0">
                  <AlertCircle className="h-4 w-4 text-rose-700" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-display font-semibold text-rose-900">{t("case_editor.review.revision_requested_title")}</div>
                  <div className="text-sm text-zinc-700 mt-1 whitespace-pre-wrap">{c.revision_note}</div>
                </div>
              </div>
            )}

            {/* Gutachter view: Inline-PDF + 3 Aktionen, wenn Akte zur Freigabe steht
                ODER bereits an den Kunden gesendet wurde (damit der Magic-Link-Status sichtbar bleibt) */}
            {!isBO && ["ready_for_gutachter", "sent_to_customer", "released", "completed"].includes(c.status) && c.final_reports?.length > 0 && (
              <ReleasePanel
                caseObj={c}
                accessToken={accessToken}
                onRelease={releaseCase}
                onRequestRevision={requestRevision}
                onSendToCustomer={sendToCustomer}
                saving={saving}
              />
            )}

            {renderReview()}
            {renderDocuments()}

            {/* Final reports — versionierte PDFs (sichtbar für alle die die Akte sehen dürfen) */}
            {(c.final_reports?.length || 0) > 0 && (
              <FinalReportsList caseObj={c} accessToken={accessToken} />
            )}

            {/* User-only submit-bar: einfacher Workflow-Abschluss für Gutachter — ganz unten nach Dokumente hochladen */}
            {!isBO && (c.status === "draft" || c.status === "in_progress" || c.status === "revision_requested") && (
              <div className="rounded-2xl border border-emerald-100 bg-emerald-50/40 p-5 mt-4 flex flex-col sm:flex-row items-center gap-4" data-testid="user-submit-bar">
                <div className="h-10 w-10 rounded-xl brand-gradient-bg flex items-center justify-center shrink-0">
                  <Upload className="h-5 w-5 text-white" />
                </div>
                <div className="flex-1 text-center sm:text-left">
                  <div className="font-display font-semibold text-zinc-900">{t("case_editor.review.user_submit_title")}</div>
                  <div className="text-sm text-zinc-600 mt-0.5">
                    {t("case_editor.review.user_submit_subtitle_pre")}<strong>{t("case_editor.review.user_submit_subtitle_btn")}</strong>{t("case_editor.review.user_submit_subtitle_post")}
                  </div>
                </div>
                <Button
                  onClick={async () => {
                    await saveCase();
                    await setStatus("uploaded");
                    // Prominent Erfolgs-Meldung mit Beschreibung. Sonner-Toast
                    // mit längerer Anzeigedauer, damit der User die Info
                    // garantiert sieht, BEVOR wir zum Dashboard zurück-
                    // navigieren. Anschließend Routing auf `/` (Dashboard),
                    // wo der User später den Status seines Gutachtens
                    // verfolgen kann.
                    toast.success(t("case_editor.review.submit_success_title"), {
                      description: t("case_editor.review.submit_success_subtitle"),
                      duration: 7000,
                    });
                    setTimeout(() => { navigate("/"); }, 1800);
                  }}
                  disabled={saving}
                  className="brand-gradient-bg text-white min-w-[160px]"
                  data-testid="user-submit-btn"
                >
                  {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
                  {t("case_editor.send")}
                </Button>
              </div>
            )}
          </div>
        );
      default: return null;
    }
  };

  const titleNode = (
    <div className="flex items-center gap-2 sm:gap-3 min-w-0">
      <Button
        variant="ghost"
        size="icon"
        onClick={() => navigate("/dashboard")}
        className="h-8 w-8 shrink-0 inline-flex"
        aria-label="Zurück zum Dashboard"
        title="Zurück zum Dashboard"
        data-testid="case-back-btn"
      >
        <ChevronLeft className="h-5 w-5" />
      </Button>
      <div className="min-w-0 flex-1">
        {/* Primärzeile: Akte + cyan-Badge — auf Mobile kompakt aber lesbar.
            `whitespace-nowrap` ohne `truncate` damit der Wert nicht
            abgeschnitten wird (KH-MU 030526 ist kurz genug). */}
        {c.case_reference ? (
          <>
            <div className="flex items-center gap-1.5 sm:gap-2 min-w-0" data-testid="case-reference-header">
              <span className="text-[10px] sm:text-xs font-semibold uppercase tracking-[0.2em] text-zinc-400 shrink-0">{t("case_editor.akte")}</span>
              <span className="font-display text-base sm:text-2xl font-bold tracking-tight px-2 sm:px-2.5 py-0.5 sm:py-1 rounded-md bg-cyan-100 text-cyan-800 whitespace-nowrap leading-tight">
                {c.case_reference}
              </span>
              {/* Kundenname direkt neben der Aktennummer — gibt sofort
                  Kontext, wessen Akte ich gerade öffne. Auf Mobile wird
                  der Name beim Überlauf truncated, damit das Layout nicht
                  bricht. */}
              {(c.customer?.name || c.customer?.surname) && (
                <span
                  className="font-display text-sm sm:text-xl font-semibold text-zinc-700 truncate min-w-0"
                  data-testid="case-customer-name-header"
                  title={[c.customer?.firstname, c.customer?.name || c.customer?.surname].filter(Boolean).join(" ")}
                >
                  <span className="text-zinc-300 mx-1.5 sm:mx-2">·</span>
                  {[c.customer?.firstname, c.customer?.name || c.customer?.surname].filter(Boolean).join(" ")}
                </span>
              )}
            </div>
            {/* Sekundärzeile mit Auftragsdatum/Titel — nur auf Desktop, auf Mobile zugunsten Platz weggelassen */}
            <div className="hidden sm:flex text-xs text-zinc-500 mt-2 items-center gap-x-2 gap-y-1 flex-wrap">
              {c.order_date && (
                <span data-testid="case-order-date" className="whitespace-nowrap">
                  <span className="text-zinc-400">{t("case_editor.order_date")}:</span>{" "}
                  <span className="font-mono">{formatGermanDate(c.order_date)}</span>
                </span>
              )}
              {c.title && c.title !== `Akte ${c.case_number}` && (
                <span className="truncate min-w-0">· {c.title}</span>
              )}
              <span className="font-mono text-zinc-400 whitespace-nowrap">· {c.case_number}</span>
            </div>
          </>
        ) : (
          <>
            <div className="font-display text-base sm:text-2xl font-bold tracking-tight truncate">{c.title}</div>
            <div className="hidden sm:flex text-xs text-zinc-500 font-mono mt-1 items-center gap-2 flex-wrap">
              {c.order_date && (
                <span><span className="text-zinc-400">{t("case_editor.order_date")}:</span> {formatGermanDate(c.order_date)} ·</span>
              )}
              <span>{c.case_number}</span>
            </div>
          </>
        )}
      </div>
    </div>
  );

  // Map BottomNav STEPS labels to case_editor.step_labels keys.
  // Allows the step sidebar to render fully translated names while
  // BottomNav uses the (short) mobileLabel.
  const stepKeyToI18n = {
    overview: "case_editor.step_labels.overview",
    customer: "case_editor.step_labels.customer",
    insurance: "case_editor.step_labels.insurance",
    vehicle: "case_editor.step_labels.vehicle",
    begutachtung: "case_editor.step_labels.begutachtung",
    inspection: "case_editor.step_labels.inspection",
    photos: "case_editor.step_labels.photos",
    signature: "case_editor.step_labels.signature",
    predamages: "case_editor.step_labels.predamages",
    calculation: "case_editor.step_labels.calculation",
    valuation: "case_editor.step_labels.valuation",
    honorar: "case_editor.step_labels.honorar",
    documentation: "case_editor.step_labels.documentation",
    review: "case_editor.step_labels.review",
    gutachten: "case_editor.step_labels.gutachten",
  };

  const stepSidebar = (
    <div className="space-y-1" data-testid="sidebar-case-steps">
      <div className="flex items-center justify-between px-2 pb-2">
        <span className="text-[10px] uppercase tracking-[0.22em] font-bold text-zinc-500">{t("case_editor.progress")}</span>
        <span className="text-[11px] font-mono text-zinc-400">
          {visibleSteps.filter((s) => completeness[s.key]).length}/{visibleSteps.length}
        </span>
      </div>
      {visibleSteps.map((s, idx) => {
        const isActive = step === s.key;
        const done = completeness[s.key];
        const Icon = s.icon;
        const i18nKey = stepKeyToI18n[s.key];
        const fullLabel = i18nKey ? t(i18nKey) : s.label;
        return (
          <button
            key={s.key}
            onClick={() => setStep(s.key)}
            data-testid={`sidebar-step-${s.key}`}
            className={cn(
              "w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition relative",
              isActive
                ? "bg-white text-[#12A9E8] border border-[#12A9E8]/30 shadow-sm"
                : "text-zinc-600 hover:text-zinc-950 hover:bg-white"
            )}
          >
            <span
              className={cn(
                "h-5 w-5 rounded-md text-[10px] font-bold flex items-center justify-center shrink-0 tabular-nums",
                isActive ? "bg-[#12A9E8]/15 text-[#12A9E8]" : done ? "bg-[#12A9E8]/10 text-[#12A9E8]" : "bg-zinc-100 text-zinc-500"
              )}
            >
              {idx + 1}
            </span>
            <Icon className="h-4 w-4 shrink-0" strokeWidth={1.75} />
            <span className="flex-1 text-left truncate">{fullLabel}</span>
            {done && <CheckCircle2 className="h-3.5 w-3.5 text-[#12A9E8] shrink-0" strokeWidth={2} />}
          </button>
        );
      })}
    </div>
  );

  return (
    <Layout
      title={titleNode}
      sidebarExtras={stepSidebar}
      sidebarExtrasTitle={t("case_editor.sidebar_title")}
      rightSlot={
        <div className="flex items-center gap-1.5 sm:gap-2">
          {/* Status-Dropdown nur auf Tablet+ — Mobile bekommt Akten-Header
              den ganzen Platz; Status kann im Abschluss-Step geändert werden. */}
          <Select value={c.status} onValueChange={setStatus}>
            <SelectTrigger
              className="hidden sm:flex w-[170px] h-9"
              data-testid="case-status-select"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {getStatusList(t).map((s) => <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>)}
            </SelectContent>
          </Select>
          <AutoSaveBadge status={autoSaveStatus} lastSavedAt={lastSavedAt} />
          <Button size="sm" variant="outline" onClick={() => saveCase()} disabled={saving} className="h-9 px-2 sm:px-3" data-testid="case-save-btn">
            {saving ? <Loader2 className="h-4 w-4 sm:mr-1 animate-spin" /> : <Save className="h-4 w-4 sm:mr-1" />}
            <span className="hidden sm:inline">{t("case_editor.save")}</span>
          </Button>
        </div>
      }
    >
      {/* Hinweis-Banner für Admin: was hat der Gutachter eingetragen? */}
      {isAdminViewingOthersCase && ownerFilled.size > 0 && (
        <div
          className="rounded-xl border border-cyan-200 bg-cyan-50/70 px-4 py-2.5 mb-4 flex items-center gap-3"
          data-testid="admin-owner-filled-hint"
        >
          <div className="h-7 w-7 rounded-md bg-cyan-100 flex items-center justify-center shrink-0">
            <User className="h-3.5 w-3.5 text-cyan-700" />
          </div>
          <div className="flex-1 text-[12px] text-cyan-900 leading-snug">
            <b>{ownerFilled.size} Felder</b> wurden vom Gutachter eingetragen — sie sind{" "}
            <span className="px-1.5 py-0.5 rounded bg-cyan-100 border border-cyan-300 text-[11px] font-mono">cyan markiert</span>.
            Auto-Scan-Felder bleiben <span className="px-1.5 py-0.5 rounded bg-emerald-100 border border-emerald-300 text-[11px] font-mono">grün</span>.
          </div>
        </div>
      )}

      <div
        className="rounded-2xl bg-zinc-50 border border-zinc-200 p-5 sm:p-6 lg:p-7 [&_input]:bg-white [&_textarea]:bg-white [&_button[role='combobox']]:bg-white"
        data-testid="step-content-bg"
      >
        {renderStep()}
      </div>

      <BottomNav
        current={step}
        onChange={(key) => {
          // Tap on Fotos: navigate AND open camera in one go (1-tap UX).
          if (key === "photos") setCameraTrigger((c) => c + 1);
          setStep(key);
        }}
        completeness={completeness}
      />

      {/* Globaler Diktat-FAB wurde auf Wunsch des Nutzers entfernt.
          Diktat-Funktion bleibt über das DictationModal verfügbar, kann aber
          aktuell nicht aus der UI getriggert werden (zukünftiges Feature). */}
      <DictationModal
        open={dictationOpen}
        onOpenChange={setDictationOpen}
        caseId={c.id}
        onApplied={(patch) => {
          // Reload local state from server so UI shows applied changes
          setC((prev) => {
            const next = { ...prev };
            for (const [k, v] of Object.entries(patch)) {
              if (k === "customer" || k === "vehicle" || k === "insurance") {
                next[k] = { ...(prev[k] || {}), ...v };
              } else {
                next[k] = v;
              }
            }
            return next;
          });
          toast.success("Akte aktualisiert");
        }}
      />
    </Layout>
  );
}
